import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import Navbar from '../components/common/Navbar';
import Avatar from '../components/common/Avatar';
import ProjectNavigation from '../components/common/ProjectNavigation';
import ProjectTimeline from '../components/common/ProjectTimeline';
import StandardEpost from '../components/emails/StandardEpost';
import {  hasPublishedCustomerCommentAfter } from '../features/estimat/utils/commentHelpers';
import { useErbjudanden, ErbjudandeRad, ErbjudandeModaler } from '../features/suppliers';
import useApiCache from '../hooks/useApiCache';
import EstimatView from './EstimatView';
import Offert from './Offert';
import SuggestTimeModal from '../components/common/SuggestTimeModal';
import { MeetingBookingModal, SkissSection } from '../features/estimat';
import { CustomerMeetingsSection } from '../features/meetings';
import { getProjectStatus as beraknaProjektStatus } from '../features/workspace/utils/projectStatus';
import { arExtra, arOriginal, visaBeteckning } from '../utils/offertSpar';

// 🚀 OPTIMERING: Fetch-funktioner utanför komponenten för stabilitet
const fetchProjects = async () => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/myproject`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });
  if (!response.ok) throw new Error('Något gick fel när projekten hämtades.');
  return response.json();
};

const fetchSharePointStatus = async () => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/underlag/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });
  if (!response.ok) throw new Error('Kunde inte kontrollera SharePoint-status');
  const data = await response.json();
  return data.isConnected;
};

const fetchCustomerEstimat = async () => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/api/estimat/customer/my-estimat`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });
  if (!response.ok) throw new Error('Kunde inte hämta estimat');
  return response.json();
};

const fetchMyActionPoints = async () => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/api/meeting-protocols/my-action-points`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });
  if (!response.ok) throw new Error('Kunde inte hämta uppgifter');
  const data = await response.json();
  return data.data || [];
};

const fetchCustomerOfferter = async (includeObsolete = false) => {
  const token = localStorage.getItem('token');
  const url = includeObsolete
    ? `${API_URL}/api/offert/customer/my-offerts?includeObsolete=true`
    : `${API_URL}/api/offert/customer/my-offerts`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });
  if (!response.ok) throw new Error('Kunde inte hämta offerter');
  return response.json();
};

const fetchCustomerProcurement = async () => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/api/procurement/customer/my-procurement`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });
  if (!response.ok) throw new Error('Kunde inte hämta procurement');
  return response.json();
};

const fetchOffertFollowupRequests = async (procurementId) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/api/procurement/${procurementId}/offert-followup-requests`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });
  if (!response.ok) throw new Error('Kunde inte hämta offertuppföljningsmöte-förslag');
  return response.json();
};

// Plocka ut den aktuella offert-uppföljningsförfrågan ur arrayen.
// MongoDB returnerar subdokument i insättningsordning, så [0] är ÄLDST — inte senaste.
// Vi prioriterar aktiva statusar (pending/new_proposal_sent/accepted) över historiska
// bokade möten, och bland flera tar vi nyaste enligt requestedAt. Det innebär att en
// kund som redan haft ett bokat uppföljningsmöte kan se en ny pending-förfrågan.
const getActiveOffertFollowupRequest = (requests) => {
  if (!Array.isArray(requests) || requests.length === 0) return null;
  const sorted = [...requests].sort(
    (a, b) => new Date(b.requestedAt || 0) - new Date(a.requestedAt || 0)
  );
  const active = sorted.find(r =>
    ['pending', 'new_proposal_sent', 'accepted'].includes(r.status)
  );
  return active || sorted[0];
};

// Flytta ut denna funktion så att den finns innan MyProjects-komponenten:
const getAllProjectRecipients = (project, mainRecipient) => {
  const recipients = [];
  // Byggkonsult(er)
  if (project.consultant && project.consultant.email) {
    recipients.push({
      name: `${project.consultant.firstName} ${project.consultant.lastName}`,
      email: project.consultant.email,
      role: 'Byggkonsult',
      type: 'consultant',
      isRequired: false,
      isMainRecipient: mainRecipient && mainRecipient.email === project.consultant.email
    });
  }
  if (project.additionalConsultants && project.additionalConsultants.length > 0) {
    project.additionalConsultants.forEach(consultant => {
      if (consultant.email) {
        recipients.push({
          name: `${consultant.firstName} ${consultant.lastName}`,
          email: consultant.email,
          role: 'Byggkonsult',
          type: 'consultant',
          isRequired: false,
          isMainRecipient: mainRecipient && mainRecipient.email === consultant.email
        });
      }
    });
  }
  // Inredningsarkitekt(er)
  if (project.architect && project.architect.email) {
    recipients.push({
      name: `${project.architect.firstName} ${project.architect.lastName}`,
      email: project.architect.email,
      role: 'Inredningsarkitekt',
      type: 'architect',
      isRequired: false,
      isMainRecipient: mainRecipient && mainRecipient.email === project.architect.email
    });
  }
  if (project.additionalArchitects && project.additionalArchitects.length > 0) {
    project.additionalArchitects.forEach(architect => {
      if (architect.email) {
        recipients.push({
          name: `${architect.firstName} ${architect.lastName}`,
          email: architect.email,
          role: 'Inredningsarkitekt',
          type: 'architect',
          isRequired: false,
          isMainRecipient: mainRecipient && mainRecipient.email === architect.email
        });
      }
    });
  }
  // Byggare (kontaktperson)
  if (project.builderContactPerson && project.builderContactPerson.email) {
    recipients.push({
      name: `${project.builderContactPerson.firstName} ${project.builderContactPerson.lastName}`,
      email: project.builderContactPerson.email,
      role: 'Byggare',
      type: 'builder',
      isRequired: false,
      isMainRecipient: mainRecipient && mainRecipient.email === project.builderContactPerson.email
    });
  }
  // Kunder
  if (project.customer && project.customer.email) {
    recipients.push({
      name: `${project.customer.firstName} ${project.customer.lastName}`,
      email: project.customer.email,
      role: 'Kund',
      type: 'customer',
      isRequired: false,
      isMainRecipient: mainRecipient && mainRecipient.email === project.customer.email
    });
  }
  if (project.additionalCustomers && project.additionalCustomers.length > 0) {
    project.additionalCustomers.forEach(customer => {
      if (customer.email) {
        recipients.push({
          name: `${customer.firstName} ${customer.lastName}`,
          email: customer.email,
          role: 'Kund',
          type: 'customer',
          isRequired: false,
          isMainRecipient: mainRecipient && mainRecipient.email === customer.email
        });
      }
    });
  }
  // Materialleverantörer
  if (project.materialSuppliers && project.materialSuppliers.length > 0) {
    project.materialSuppliers.forEach(ms => {
      if (ms.contactPerson && ms.contactPerson.email) {
        recipients.push({
          name: `${ms.contactPerson.firstName} ${ms.contactPerson.lastName}`,
          email: ms.contactPerson.email,
          role: 'Materialleverantör',
          type: 'materialSupplier',
          isRequired: false,
          isMainRecipient: mainRecipient && mainRecipient.email === ms.contactPerson.email
        });
      }
    });
  }
  // Underleverantörer (UE)
  if (project.subcontractors && project.subcontractors.length > 0) {
    project.subcontractors.forEach(ue => {
      if (ue.email) {
        recipients.push({
          name: `${ue.firstName} ${ue.lastName}`,
          email: ue.email,
          role: 'Underleverantör',
          type: 'subcontractor',
          company: ue.companyName || '',
          isRequired: false,
          isMainRecipient: mainRecipient && mainRecipient.email === ue.email
        });
      }
    });
  }
  // Projektörer
  if (project.projectors && project.projectors.length > 0) {
    project.projectors.forEach(p => {
      if (p.email) {
        recipients.push({
          name: `${p.firstName} ${p.lastName}`,
          email: p.email,
          role: 'Projektör',
          type: 'projector',
          company: p.companyName || '',
          isRequired: false,
          isMainRecipient: mainRecipient && mainRecipient.email === p.email
        });
      }
    });
  }
  // Markera rätt mainRecipient
  return recipients.map(r => ({
    ...r,
    isMainRecipient: mainRecipient && mainRecipient.email === r.email
  }));
};

const MyProjects = () => {
  // Helper för att få userId för cache-nycklar (innan useAuth körs)
  const getUserId = () => {
    const token = localStorage.getItem('token');
    if (!token) return 'anon';
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id || 'anon';
    } catch {
      return 'anon';
    }
  };
  const userId = getUserId();
  // 🚀 OPTIMERING: Cache-baserade API-anrop för bättre prestanda
  const { 
    data: projects, 
    loading: projectsLoading, 
    refresh: refreshProjects 
  } = useApiCache(
    `myprojects_projects_${userId}`,
    fetchProjects,
    300000, // 5 minuter cache
    [], // ingen dependencies
    [] // defaultValue: tom array
  );
  
  const { 
    data: sharepointConnected, 
    refresh: refreshSharePoint 
  } = useApiCache(
    `myprojects_sharepoint_${userId}`,
    fetchSharePointStatus,
    300000, // 5 minuter cache
    [], // ingen dependencies
    false // defaultValue: false
  );
  
  // 🚀 OPTIMERING: Compute loading state från cache
  const loading = projectsLoading;
  const [isOffersExpanded, setIsOffersExpanded] = useState(false);
  const [expandedSupplier, setExpandedSupplier] = useState(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingTabIndex, setPendingTabIndex] = useState(null);
  const shoppingListRef = useRef();
  const rumsaRef = useRef();
  const navigate = useNavigate();
  const { user } = useAuth();
  const socket = useSocket();
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  // Dynamisk initial state för estimat - minimerad om det finns skickade offerter
  const [expandedEstimat, setExpandedEstimat] = useState(() => {
    // Kommer att uppdateras i useEffect när offerter laddas
    return true;
  });
  const [estimat, setEstimat] = useState([]);
  const [, setEstimatLoading] = useState(false);
  // Vilket estimat kunden tittar på. Ett lead kan ha flera parallella
  // estimat ("Ovanplan", "Bottenplan") — null betyder det första i listan.
  const [valtEstimatId, setValtEstimatId] = useState(null);
  // Ett kort per estimatspår. Backend skickar redan alla kundvända estimat;
  // äldre versioner inom ett spår är arkiverade och kommer aldrig hit, men vi
  // grupperar ändå på variantKey så listan inte kan få dubbletter.
  const estimatSpar = React.useMemo(() => {
    if (!Array.isArray(estimat)) return [];
    const perSpar = new Map();
    estimat.forEach(e => {
      const nyckel = e.variantKey ?? null;
      if (!perSpar.has(nyckel)) perSpar.set(nyckel, e);
    });
    return Array.from(perSpar.values());
  }, [estimat]);

  const valtEstimat = React.useMemo(() => {
    if (!estimatSpar.length) return null;
    if (valtEstimatId) {
      const traff = estimatSpar.find(e => String(e._id) === String(valtEstimatId));
      if (traff) return traff;
    }
    return estimatSpar[0];
  }, [estimatSpar, valtEstimatId]);

  const [expandedOfferter, setExpandedOfferter] = useState(false); // Börja som minimerad (collapsed)
  const [expandedUnderlag, setExpandedUnderlag] = useState(true); // BK Underlag (skiss) - börja expanderad
  // Finns det något i Underlag-mappen? Sätts av SkissSection efter hämtningen.
  // Utan detta visades en tom Underlag-ruta för kunder vars upphandling saknar skiss.
  const [harUnderlag, setHarUnderlag] = useState(false);
  const [expandedOffertMessage, setExpandedOffertMessage] = useState(true); // Meddelande från Leadme - börja expanderad, kan minimeras
  const [expandedBookedOffertFollowup, setExpandedBookedOffertFollowup] = useState(false);
  const [offerter, setOfferter] = useState([]);
  const [offerterLoading, setOfferterLoading] = useState(false);
  // Sant efter första hämtningen — så en länk med ?offert= väntar på listan
  const [offerterHamtade, setOfferterHamtade] = useState(false);
  const [showObsoleteOffers, setShowObsoleteOffers] = useState(false); // 🔥 Toggle för historiska offerter
  const [hiddenObsoleteCount, setHiddenObsoleteCount] = useState(0); // 🔥 Antal dolda historiska offerter
  const [selectedOffert, setSelectedOffert] = useState(null);
  const [showOffertModal, setShowOffertModal] = useState(false);
  // Jämförelse-funktionalitet
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [selectedOffersForComparison, setSelectedOffersForComparison] = useState([]);
  const [showComparisonView, setShowComparisonView] = useState(false);
  const [syncScrollEnabled, setSyncScrollEnabled] = useState(false); // Av som standard
  const scrollRefs = useRef([]);
  
  // Synkroniserad scrollning för jämförelse-vy
  const handleSyncScroll = useCallback((scrollingIndex, scrollTop) => {
    if (!syncScrollEnabled) return; // Hoppa över om synk är avstängd
    scrollRefs.current.forEach((ref, index) => {
      if (ref && index !== scrollingIndex) {
        ref.scrollTop = scrollTop;
      }
    });
  }, [syncScrollEnabled]);
  
  // Offertuppföljningsmöte state
  const [showOffertFollowupModal, setShowOffertFollowupModal] = useState(false);
  const [isOffertFollowupExpanded, setIsOffertFollowupExpanded] = useState(true);
  const [toast, setToast] = useState(null);
  const [offertFollowupRequests, setOffertFollowupRequests] = useState([]);
  const [, setOffertFollowupLoading] = useState(false);

  // Estimat mötesbokning state (för "Boka genomgång"-knappen)
  const [showEstimatMeetingModal, setShowEstimatMeetingModal] = useState(false);
  const [estimatMeetingSlots, setEstimatMeetingSlots] = useState([]);
  const [estimatMeetingSubmitting, setEstimatMeetingSubmitting] = useState(false);
  
  // Toast-funktion för att visa meddelanden
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000); // Visa i 4 sekunder
  };
  
  const [procurement, setProcurement] = useState(null);
  const [, setProcurementLoading] = useState(false);
  const [projektgenomgang, setProjektgenomgang] = useState(null);
  const [hvfExists, setHvfExists] = useState(false);
  // Leverantörserbjudanden — logiken delas med sidan /myproject/:id/erbjudanden
  const {
    erbjudanden,
    contactRequests,
    handleContactRequest,
    handleShowInfo,
    modalProps,
  } = useErbjudanden({ projects, procurement });
  // Lägg till state för materialleverantörs-e-postmodal
  const [showMaterialSupplierEmailModal, setShowMaterialSupplierEmailModal] = useState(false);
  const [selectedMaterialSupplierContact, setSelectedMaterialSupplierContact] = useState(null);
  const [selectedMaterialSupplierProject, setSelectedMaterialSupplierProject] = useState(null);
  // Lägg till state för dokumentstatus per projekt

  // State för kundens actionpoints/att göra
  const [myActionPoints, setMyActionPoints] = useState([]);
  const [loadingActionPoints, setLoadingActionPoints] = useState(false);
  const [editingActionPoint, setEditingActionPoint] = useState(null);
  const [editFormData, setEditFormData] = useState({ title: '', content: '', dueDate: '' });
  const [isAddingTodo, setIsAddingTodo] = useState(false);
  const [newTodoData, setNewTodoData] = useState({ title: '', content: '', dueDate: '' });

  // Mobile detection
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // State för expanderade sektioner i kundvy (per projekt)
  const [expandedSections, setExpandedSections] = useState({});

  // Default expanded state: Desktop always expanded, Mobile collapsed except Möten and Att göra
  const getDefaultExpanded = (section) => {
    if (!isMobile) return true;
    // On mobile, only Möten and Att göra are expanded by default
    return section === 'moten' || section === 'attgora';
  };

  // Toggle sektion expansion
  const toggleSection = (projectId, section) => {
    setExpandedSections(prev => {
      const current = prev[projectId]?.[section];
      const defaultVal = getDefaultExpanded(section);
      const isCurrentlyExpanded = current !== undefined ? current : defaultVal;
      return {
        ...prev,
        [projectId]: {
          ...prev[projectId],
          [section]: !isCurrentlyExpanded
        }
      };
    });
  };

  // Kolla om en sektion är expanderad
  const isSectionExpanded = (projectId, section) => {
    const val = expandedSections[projectId]?.[section];
    if (val !== undefined) return val;
    return getDefaultExpanded(section);
  };


  const handleTabChange = async (newPath) => {
    // Kontrollera om det finns osparade ändringar
    const hasUnsavedChanges = 
      (shoppingListRef.current?.hasUnsavedChanges) || 
      (rumsaRef.current?.hasUnsavedChanges);

    if (hasUnsavedChanges) {
      setPendingTabIndex(newPath);
      setShowUnsavedDialog(true);
    } else {
      navigate(newPath);
    }
  };

  const toggleProject = (projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };


  // Ta bort denna useEffect - datan laddas nu via useApiCache

  // Ta bort denna useEffect - favoriter laddas nu via useApiCache

  useEffect(() => {
    if (projects.length === 1) {
      setExpandedProjects(new Set([projects[0]._id]));
    }
  }, [projects]);

  // 🚀 REAL-TIME: Lyssna på socket-event när projekt skapas
  useEffect(() => {
    if (!socket || user?.role !== 'kund') return;

    const handleProjectCreated = (data) => {
      // Invalidera cache och hämta ny data
      refreshProjects();
    };

    socket.on('project:created', handleProjectCreated);

    return () => {
      socket.off('project:created', handleProjectCreated);
    };
  }, [socket, user, refreshProjects]);

  // Estimat ska vara minimerat som default; ingen auto-expand

  // Hämta estimat för kunder
  useEffect(() => {
    if (user?.role === 'kund') {
      setEstimatLoading(true);
      fetchCustomerEstimat()
        .then(data => {
          setEstimat(data);
        })
        .catch(error => {
          console.error('Fel vid hämtning av estimat:', error);
          setEstimat([]);
        })
        .finally(() => {
          setEstimatLoading(false);
        });
    }
  }, [user]);

  // Hämta kundens actionpoints (från protokoll + egna custom todos från databas)
  useEffect(() => {
    if (user?.role === 'kund') {
      setLoadingActionPoints(true);
      const token = localStorage.getItem('token');

      // Hämta både protokoll-actionpoints och custom todos parallellt
      Promise.all([
        fetchMyActionPoints(),
        fetch(`${API_URL}/api/todos`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => res.ok ? res.json() : [])
      ])
        .then(([protocolData, customTodos]) => {
          // Markera som isCustom ENDAST om todo saknar koppling till källa
          // (protokoll, kanban-task, brief). Annars ska "Från X"-badgen visas.
          const markedCustomTodos = customTodos.map(todo => ({
            ...todo,
            isCustom: !todo.sourceKanbanTaskId && !todo.sourceProtocolId && !todo.sourceBriefId,
            projectId: todo.project?._id || todo.project
          }));
          const allTodos = [...protocolData, ...markedCustomTodos];
          setMyActionPoints(allTodos);
        })
        .catch(error => {
          console.error('Fel vid hämtning av uppgifter:', error);
          setMyActionPoints([]);
        })
        .finally(() => {
          setLoadingActionPoints(false);
        });
    }
  }, [user]);

  /**
   * Toggla en punkt i Att göra som klar/ej klar.
   *
   * Listan blandar två sorters poster: actionpunkter från mötesprotokoll och
   * fristående todos (t.ex. en uppgift byggkonsulten skapat åt kunden). Bara de
   * förra har ett protocolId. Utan den här skillnaden gick alla anrop till
   * protokoll-endpointen, och för en todo blev URL:en ".../undefined/sections/..."
   * vilket gav CastError i backend och lämnade rutan omarkerad.
   */
  const toggleActionPoint = async (protocolId, sectionId, completed) => {
    try {
      const token = localStorage.getItem('token');
      const url = protocolId
        ? `${API_URL}/api/meeting-protocols/${protocolId}/sections/${sectionId}/toggle`
        : `${API_URL}/api/todos/${sectionId}`;

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ completed })
      });
      if (!response.ok) throw new Error('Kunde inte uppdatera');

      // Uppdatera lokalt state
      setMyActionPoints(prev => prev.map(ap =>
        ap._id === sectionId ? { ...ap, completed, completedAt: completed ? new Date().toISOString() : null } : ap
      ));
    } catch (error) {
      console.error('Fel vid toggle av actionpunkt:', error);
    }
  };

  // Uppdatera actionpoint (titel, beskrivning, deadline)
  // Samma uppdelning som i toggleActionPoint: todos har ingen protokollkoppling
  const updateActionPoint = async (protocolId, sectionId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const url = protocolId
        ? `${API_URL}/api/meeting-protocols/${protocolId}/sections/${sectionId}/update-from-kanban`
        : `${API_URL}/api/todos/${sectionId}`;

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error('Kunde inte uppdatera');

      // Uppdatera lokalt state
      setMyActionPoints(prev => prev.map(ap =>
        ap._id === sectionId ? { ...ap, ...updates } : ap
      ));
      setEditingActionPoint(null);
      setEditFormData({ title: '', content: '', dueDate: '' });
    } catch (error) {
      console.error('Fel vid uppdatering av actionpunkt:', error);
    }
  };

  // Starta redigering av actionpoint
  const startEditingActionPoint = (ap) => {
    setEditingActionPoint(ap._id);
    setEditFormData({
      title: ap.title || '',
      content: ap.content || '',
      dueDate: ap.dueDate ? new Date(ap.dueDate).toISOString().split('T')[0] : ''
    });
  };

  // Avbryt redigering
  const cancelEditingActionPoint = () => {
    setEditingActionPoint(null);
    setEditFormData({ title: '', content: '', dueDate: '' });
  };

  // Lägg till egen todo-punkt för kund (sparas i databas)
  const addCustomTodo = async (projectId) => {
    if (!newTodoData.title.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/todos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId,
          title: newTodoData.title.trim(),
          content: newTodoData.content.trim() || '',
          dueDate: newTodoData.dueDate || null
        })
      });

      if (!response.ok) throw new Error('Kunde inte skapa todo');

      const newTodo = await response.json();
      // Markera som custom och lägg till i state
      setMyActionPoints(prev => [...prev, {
        ...newTodo,
        isCustom: true,
        projectId: newTodo.project?._id || newTodo.project
      }]);
      setNewTodoData({ title: '', content: '', dueDate: '' });
      setIsAddingTodo(false);
    } catch (error) {
      console.error('Fel vid skapande av todo:', error);
    }
  };

  // Uppdatera custom todo (i databas)
  const updateCustomTodo = async (todoId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/todos/${todoId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Kunde inte uppdatera todo');

      setMyActionPoints(prev => prev.map(ap =>
        ap._id === todoId ? { ...ap, ...updates } : ap
      ));
      setEditingActionPoint(null);
      setEditFormData({ title: '', content: '', dueDate: '' });
    } catch (error) {
      console.error('Fel vid uppdatering av custom todo:', error);
    }
  };

  // Toggle custom todo (i databas)
  const toggleCustomTodo = async (todoId, completed) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/todos/${todoId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ completed })
      });

      if (!response.ok) throw new Error('Kunde inte uppdatera todo');

      setMyActionPoints(prev => prev.map(ap =>
        ap._id === todoId ? { ...ap, completed, completedAt: completed ? new Date().toISOString() : null } : ap
      ));
    } catch (error) {
      console.error('Fel vid toggle av custom todo:', error);
    }
  };

  // Ta bort custom todo (från databas)
  const deleteCustomTodo = async (todoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/todos/${todoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Kunde inte ta bort todo');

      setMyActionPoints(prev => prev.filter(ap => ap._id !== todoId));
    } catch (error) {
      console.error('Fel vid borttagning av custom todo:', error);
    }
  };

  // Funktion för att uppdatera offerter
  const refreshOfferter = useCallback(async () => {
    if (user?.role === 'kund') {
      setOfferterLoading(true);
      try {
        const data = await fetchCustomerOfferter(showObsoleteOffers);
        const offerterData = data.data || [];
        setOfferter(offerterData);

        // 🔥 Uppdatera antal dolda historiska offerter
        if (data.meta?.hiddenObsoleteCount !== undefined) {
          setHiddenObsoleteCount(data.meta.hiddenObsoleteCount);
        }

        // ✅ Om det finns nya offerter (status === 'sent'), expandera sektionen automatiskt
        const hasNewOffers = offerterData.some(o => o.status === 'sent');
        if (hasNewOffers) {
          setExpandedOfferter(true);
        }
      } catch (error) {
        console.error('❌ Fel vid hämtning av offerter:', error);
        setOfferter([]);
      } finally {
        setOfferterLoading(false);
        setOfferterHamtade(true);
      }
    }
  }, [user, showObsoleteOffers]);

  // Hämta offerter för kunder
  useEffect(() => {
    refreshOfferter();
  }, [refreshOfferter]);

  /**
   * Offertnotisen leder hit med ?offert=<id> och öppnar offertmodalen — samma
   * vy som under Projektdokument. Tidigare ledde den till den fristående
   * offertsidan, där Tillbaka skickade kunden till adminsidan.
   *
   * Väntar på offertlistan för att få med byggföretagets namn i rubriken.
   * Finns offerten inte i listan (t.ex. en dold historisk version) öppnas den
   * ändå på id — Offert hämtar sin egen data.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const offertFranLank = searchParams.get('offert');
  useEffect(() => {
    if (!offertFranLank || user?.role !== 'kund' || !offerterHamtade) return;
    const offert = offerter.find(o => String(o._id) === offertFranLank);
    setSelectedOffert(offert || { _id: offertFranLank });
    setShowOffertModal(true);

    // Parametern tas bort så modalen inte öppnas igen vid omladdning
    const kvar = new URLSearchParams(searchParams);
    kvar.delete('offert');
    setSearchParams(kvar, { replace: true });
  }, [offertFranLank, user?.role, offerterHamtade, offerter, searchParams, setSearchParams]);

  // Lyssna på socket-uppdateringar för nya offerter
  useEffect(() => {
    if (socket && user?.role === 'kund') {
      const handleOffertUpdate = () => {
        refreshOfferter();
      };

      socket.on('offert_received', handleOffertUpdate);
      socket.on('offert_updated', handleOffertUpdate);

      return () => {
        socket.off('offert_received', handleOffertUpdate);
        socket.off('offert_updated', handleOffertUpdate);
      };
    }
  }, [socket, user, refreshOfferter]);

  // Uppdatera estimat-expansion baserat på offerter
  useEffect(() => {
    if (user?.role === 'kund' && Array.isArray(offerter)) {
      const hasSentOffers = offerter.some(offer => 
        ['sent', 'viewed', 'accepted', 'declined', 'commented'].includes(offer.status)
      );
      // Debug removed
      if (hasSentOffers) {
        // Om det finns skickade offerter, minimera estimat som standard
        setExpandedEstimat(false);
        
        // Expandera offerter som standard BARA om projektet INTE är konverterat
        const hasConvertedProject = projects && projects.length > 0;
        setExpandedOfferter(!hasConvertedProject);
      }
    }
  }, [offerter, user, projects]);

  // Memoized timeline data för att säkerställa uppdatering när offerter laddas
  const timelineData = useMemo(() => {
    if (user?.role !== 'kund' || !Array.isArray(estimat) || estimat.length === 0) {
      return null;
    }

    const latest = estimat[0];
    if (!latest) return null;

    const hasFollowup = Boolean(latest?.estimatFollowup?.teamsJoinUrl || latest?.estimatFollowup?.meetingAt || latest?.estimatFollowup?.selectedSlot);
    const isApproved = (latest?.status === 'approved') || (latest?.customerResponse?.status === 'approved' || latest?.customerResponse?.action === 'approved');
    const inProcurement = projects.length === 0;
    const homeVisitAt = procurement?.homeVisitWorkflow?.booking?.meetingAt ? new Date(procurement.homeVisitWorkflow.booking.meetingAt) : null;
    
    const steps = inProcurement
      ? ['Förstudie','Estimat','Uppföljning Estimat','Hembesök','Offert','Offertuppföljning','Boka byggare']
      : ['Projektering','Projektgenomgång','Avtal','Byggstart'];

    // Kolla om det finns skickade offerter
    const hasSentOffers = Array.isArray(offerter) && offerter.some(offer => 
      ['sent', 'viewed', 'accepted', 'declined', 'commented'].includes(offer.status)
    );

    return {
      latest,
      hasFollowup,
      isApproved,
      inProcurement,
      homeVisitAt,
      steps,
      hasSentOffers,
      offerter,
      projektgenomgang,
      hvfExists
    };
  }, [user, estimat, projects, procurement, offerter, projektgenomgang, hvfExists]);

  // Hämta procurement för kunder (för hembesök-data)
  useEffect(() => {
    if (user?.role === 'kund') {
      setProcurementLoading(true);
      fetchCustomerProcurement()
        .then(data => {
          setProcurement(data);
        })
        .catch(error => {
          console.error('Fel vid hämtning av procurement:', error);
          setProcurement(null);
        })
        .finally(() => {
          setProcurementLoading(false);
        });
    }
  }, [user]);

  // Hämta möten och HVF-dokument för projekttidslinjen
  useEffect(() => {
    if (user?.role !== 'kund' || !projects || projects.length === 0) return;
    const projectId = projects[0]._id;
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    // Hämta möten
    fetch(`${API_URL}/api/meetings/project/${projectId}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const meetings = data.meetings || data;
        if (Array.isArray(meetings)) {
          const pgMeeting = meetings.find(m =>
            m.type === 'projektgenomgang' && !['draft', 'cancelled'].includes(m.status)
          );
          if (pgMeeting) {
            setProjektgenomgang({
              scheduledAt: pgMeeting.scheduledAt,
              status: pgMeeting.status
            });
          }
        }
      })
      .catch(err => console.error('Fel vid hämtning av möten för tidslinje:', err));

    // Hämta HVF-dokument
    fetch(`${API_URL}/api/hvf/documents/${projectId}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const docs = Array.isArray(data) ? data : (data.documents || []);
        setHvfExists(docs.length > 0);
      })
      .catch(err => console.error('Fel vid hämtning av HVF för tidslinje:', err));
  }, [user, projects]);

  // Hämta offertuppföljningsmöte-förslag när procurement laddas
  useEffect(() => {
    if (user?.role === 'kund' && procurement?._id) {
      setOffertFollowupLoading(true);
      fetchOffertFollowupRequests(procurement._id)
        .then(data => {
          if (data.success) {
            setOffertFollowupRequests(data.data || []);
          }
        })
        .catch(error => {
          console.error('Fel vid hämtning av offertuppföljningsmöte-förslag:', error);
          setOffertFollowupRequests([]);
        })
        .finally(() => {
          setOffertFollowupLoading(false);
        });
    }
  }, [user, procurement]);

  // SharePoint-status laddas nu via useApiCache


  // Generera mötestider för offertuppföljning (samma logik som EstimatView)
  const generateOffertMeetingOptions = () => {
    const options = [];
    const today = new Date();
    const twoWeeksFromNow = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
    
    for (let d = new Date(today); d <= twoWeeksFromNow; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay(); // 0 = söndag, 1 = måndag, ..., 6 = lördag
      
      // Endast onsdag (3), torsdag (4), fredag (5)
      if (dayOfWeek >= 3 && dayOfWeek <= 5) {
        const dateStr = d.toLocaleDateString('sv-SE', { weekday: 'short', day: '2-digit', month: '2-digit' });
        
        // Lägg till 11:00-12:00 och 14:00-15:00
        options.push(`${dateStr} kl 11:00–12:00`);
        options.push(`${dateStr} kl 14:00–15:00`);
      }
    }
    
    return options;
  };

  // Skicka mötesförslag för estimat-genomgång
  const handleEstimatMeetingSubmit = async (slots) => {
    const latest = Array.isArray(estimat) && estimat.length > 0 ? estimat[0] : null;
    if (!latest || slots.length < 2) return;

    setEstimatMeetingSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/estimat/${latest._id}/customer-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'feedback',
          comments: {},
          overallFeedback: '',
          meetingRequest: {
            note: '',
            slots: slots
          }
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Kunde inte skicka mötesförslag');

      // Uppdatera estimat-listan
      if (data.estimat) {
        setEstimat(prev => prev.map(e => e._id === data.estimat._id ? data.estimat : e));
      }

      setShowEstimatMeetingModal(false);
      setEstimatMeetingSlots([]);
      showToast('Dina tidsförslag har skickats till byggkonsulten!', 'success');

      // Ladda om sidan för att uppdatera vyn
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error('Fel vid skickning av mötesförslag:', error);
      showToast(error.message || 'Kunde inte skicka mötesförslag', 'error');
    } finally {
      setEstimatMeetingSubmitting(false);
    }
  };

  // Hantera offertuppföljningsmöte-förfrågan (för kunder via nya globala systemet)
  const handleOffertFollowupSubmit = async (proposals, message) => {
    try {
      const token = localStorage.getItem('token');
      
      // Konvertera proposals till meetingSlots format
      const meetingSlots = proposals.map(p => p.displayText);
      
      const response = await fetch(`${API_URL}/api/procurement/${procurement._id}/offert-followup-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          meetingSlots,
          notes: message || 'Önskar offertuppföljningsmöte'
        })
      });

      if (!response.ok) {
        throw new Error('Kunde inte skicka förfrågan');
      }

      setShowOffertFollowupModal(false);
      
      // Uppdatera listan med offertuppföljningsmöte-förslag
      if (procurement?._id) {
        fetchOffertFollowupRequests(procurement._id)
          .then(data => {
            if (data.success) {
              setOffertFollowupRequests(data.data || []);
            }
          })
          .catch(error => {
            console.error('Fel vid uppdatering av offertuppföljningsmöte-förslag:', error);
          });
      }
      
      showToast('Förfrågan om offertuppföljningsmöte har skickats till din byggkonsult! 📅', 'success');
    } catch (error) {
      console.error('Fel vid skickande av offertuppföljningsmöte-förfrågan:', error);
      showToast('Kunde inte skicka förfrågan. Försök igen.', 'error');
      throw error; // Kasta vidare så SuggestTimeModal kan hantera det
    }
  };

  // Hantera acceptering av offertuppföljningsmöte-förslag
  const handleAcceptOffertFollowupProposal = async (requestId, selectedProposal) => {
    try {
      const token = localStorage.getItem('token');

      // Backend förväntar sig { selectedProposal: { displayText: "..." } }
      // Hantera både objekt och strängar
      const displayText = typeof selectedProposal === 'object'
        ? (selectedProposal.displayText || selectedProposal.text || JSON.stringify(selectedProposal))
        : selectedProposal;

      const response = await fetch(`${API_URL}/api/procurement/${procurement._id}/offert-followup-requests/${requestId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          selectedProposal: { displayText }
        })
      });

      if (!response.ok) {
        throw new Error('Kunde inte acceptera förslaget');
      }

      await response.json();

      // Uppdatera listan med offertuppföljningsmöte-förslag
      if (procurement?._id) {
        fetchOffertFollowupRequests(procurement._id)
          .then(data => {
            if (data.success) {
              setOffertFollowupRequests(data.data || []);
            }
          })
          .catch(error => {
            console.error('Fel vid uppdatering av offertuppföljningsmöte-förslag:', error);
          });
      }

      showToast(`Tidsförslag accepterat! Din byggkonsult kommer att boka mötet för ${selectedProposal.displayText}. 📅`, 'success');
    } catch (error) {
      console.error('Fel vid acceptering av offertuppföljningsmöte-förslag:', error);
      showToast('Kunde inte acceptera förslaget. Försök igen.', 'error');
    }
  };


  // Checking for pending updates on component mount
  useEffect(() => {
    const pendingUpdate = localStorage.getItem('pendingStateUpdate');
    if (pendingUpdate) {
      try {
        const { type, timestamp } = JSON.parse(pendingUpdate);
        // Only apply updates that are less than 5 minutes old
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          if (type === 'sharepoint_status') {
            // Force a check of SharePoint status
            // SharePoint-status uppdateras via cache
            refreshSharePoint();
          }
        }
      } catch (e) {
        console.error('Error parsing pending update:', e);
      }
      
      // Clear the pending update
      localStorage.removeItem('pendingStateUpdate');
    }
  }, [refreshSharePoint]);

  // Ta bort debug-loggning för projektobjekt

  // SharePoint-statuskontrollen låg här. Den slog två anrop per expanderat
  // projekt mot Underlag och Avtal, lade resultatet i documentStatuses — och
  // ingenting renderade det. Sidan visar inte längre dokumentstatus, så slingan
  // gjorde bara nätverksanrop och fyllde backendloggen med "Kunde inte nå
  // mappen" när en sökväg inte stämde.


  // Debug removed

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center min-h-[300px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-leadme-pink mb-6"></div>
            <div className="w-full max-w-md">
              <div className="h-8 bg-pink-100 rounded-lg mb-4 animate-pulse"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="h-32 bg-pink-50 rounded-lg animate-pulse"></div>
                <div className="h-32 bg-pink-50 rounded-lg animate-pulse"></div>
                <div className="h-32 bg-pink-50 rounded-lg animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <Navbar />
      <div className="container mx-auto px-4 sm:px-8 py-8">

      <div className="mb-6">
       {/* Tidslinje för kunder - visa överst */}
       {timelineData && (
          <ProjectTimeline 
            timelineData={timelineData}
            project={projects[0]} // Första projektet (om det finns)
            procurement={procurement}
            offertFollowupRequests={offertFollowupRequests}
          />
        )}
      </div>

      <div className="mb-6">
        {/* Välkomstsektion för nya kunder */}
          {projects.length === 0 && (() => {
          const hidden = (() => { try { return localStorage.getItem('hide_welcome_card') === '1'; } catch (_) { return false; } })();
          if (hidden) return null;
          return (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-12 relative">
              <button
                aria-label="Dölj välkomstkort"
                title="Dölj"
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                onClick={() => { try { localStorage.setItem('hide_welcome_card','1'); } catch(_){}; window.location.reload(); }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-8">
              <h1 className="text-3xl font-bold text-gray-800">Välkommen till Leadme</h1>
              <p className="text-gray-600 mt-3">
                Du har ännu inte blivit tilldelad något projekt. Under tiden kan du utforska ditt estimat och våra samarbetspartners 
                och deras erbjudanden nedan. När ditt projekt startar kommer du att få tillgång till alla projektverktyg 
                och dokument här.
              </p>
            </div>
          </div>
          );
        })()}
      </div>

        {/* Projektlista */}
        {projects.map(project => (
          <div key={project._id} className="mb-6 mt-6">
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <button
              onClick={() => toggleProject(project._id)}
              className={`w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors ${
                projects.length === 1 ? 'cursor-default' : ''
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div className="min-w-0 text-left">
                  <span className="font-semibold text-gray-900">Mitt projekt</span>
                  <p className="text-xs sm:text-sm text-gray-500">
                    #{project.projectId} · {project.address}{project.postCode ? `, ${project.postCode}` : ''}{project.city ? `, ${project.city}` : ''} · {project.projectType}
                  </p>
                </div>
              </div>
              {projects.length > 1 && (
                <div className="flex items-center gap-2">
                  <svg
                    className={`w-5 h-5 text-gray-400 transform transition-transform ${
                      expandedProjects.has(project._id) ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              )}
            </button>

            {/* Expanderat innehåll */}
            {expandedProjects.has(project._id) && (
              <div className="bg-white overflow-hidden">
                <ProjectNavigation
                  currentPage="overview"
                  projectId={project._id}
                  project={project}
                  hasAtas={project?.hasAtas}
                  onTabChange={handleTabChange}
                  hasSharepointConnection={sharepointConnected}
                />
                
                <div className="p-4 space-y-4">
                  {/* Rad 1: Projektinformation och Ditt team */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Projektinformation - expanderbar */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection(project._id, 'projektinfo')}
                        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <span className="font-semibold text-gray-900">Projektinformation</span>
                        </div>
                        <svg
                          className={`w-5 h-5 text-gray-500 transition-transform ${isSectionExpanded(project._id, 'projektinfo') ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isSectionExpanded(project._id, 'projektinfo') && (
                        <div className="px-4 py-3 bg-white space-y-3">
                          <InfoRow
                            label="Status"
                            value={getProjectTimeStatus(project).status}
                            message={getProjectTimeStatus(project).message}
                            messageColor={getProjectTimeStatus(project).color}
                          />
                          <InfoRow
                            label="Startdatum"
                            value={
                              (project.timeline?.status !== 'missing' && project.timeline?.exists && project.timeline?.startDate)
                                ? new Date(project.timeline.startDate).toLocaleDateString('sv-SE')
                                : (project.startDate ? new Date(project.startDate).toLocaleDateString('sv-SE') : 'Saknas')
                            }
                          />
                          <InfoRow
                            label="Slutdatum"
                            value={
                              (project.timeline?.status !== 'missing' && project.timeline?.exists && project.timeline?.endDate)
                                ? (
                                  project.timeline?.originalEndDate &&
                                  new Date(project.timeline.originalEndDate).getTime() !== new Date(project.timeline.endDate).getTime()
                                    ? `${new Date(project.timeline.endDate).toLocaleDateString('sv-SE')} (${new Date(project.timeline.originalEndDate).toLocaleDateString('sv-SE')})`
                                    : new Date(project.timeline.endDate).toLocaleDateString('sv-SE')
                                )
                                : (project.endDate ? new Date(project.endDate).toLocaleDateString('sv-SE') : 'Saknas')
                            }
                            message={
                              (project.timeline?.status !== 'missing' && project.timeline?.exists && project.timeline?.originalEndDate &&
                                new Date(project.timeline.originalEndDate).getTime() !== new Date(project.timeline.endDate).getTime())
                                ? 'Ursprungligt datum inom parentes'
                                : null
                            }
                            messageColor="text-orange-600"
                          />
                        </div>
                      )}
                    </div>

                    {/* Ditt team - expanderbar */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection(project._id, 'team')}
                        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <span className="font-semibold text-gray-900">Ditt team</span>
                        </div>
                        <svg
                          className={`w-5 h-5 text-gray-500 transition-transform ${isSectionExpanded(project._id, 'team') ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isSectionExpanded(project._id, 'team') && (
                        <div className="px-4 py-3 bg-white space-y-4">
                          <div className="pb-3 border-b border-gray-100">
                            <div className="space-y-3">
                              {project.consultant && (
                                <TeamMember
                                  entity={project.consultant}
                                  name={project.consultant.firstName && project.consultant.lastName ? `${project.consultant.firstName} ${project.consultant.lastName}` : project.consultant.email ? project.consultant.email.split('@')[0] : 'Byggkonsult'}
                                  role="Byggkonsult"
                                  email={project.consultant.email}
                                  company="Leadme AB"
                                  user={user}
                                  project={project}
                                  phone={project.consultant.phone}
                                />
                              )}
                              {project.additionalConsultants && project.additionalConsultants.length > 0 && (
                                project.additionalConsultants.map(consultant => (
                                  <TeamMember
                                    key={consultant._id}
                                    entity={consultant}
                                    name={consultant.firstName && consultant.lastName ? `${consultant.firstName} ${consultant.lastName}` : consultant.email ? consultant.email.split('@')[0] : 'Byggkonsult'}
                                    role="Byggkonsult"
                                    email={consultant.email}
                                    company="Leadme AB"
                                    user={user}
                                    project={project}
                                    phone={consultant.phone}
                                  />
                                ))
                              )}
                            </div>
                          </div>

                          {/* Inredningsarkitekter */}
                          {(project.architect || (project.additionalArchitects && project.additionalArchitects.length > 0)) && (
                            <div className="pb-3 border-b border-gray-100">
                              {project.additionalArchitects && project.additionalArchitects.length > 0 && (
                                <div className="text-sm font-medium text-gray-600 mb-2">Inredningsarkitekter</div>
                              )}
                              <div className="space-y-3">
                                {project.architect && (
                                  <TeamMember
                                    entity={project.architect}
                                    name={project.architect.firstName && project.architect.lastName ? `${project.architect.firstName} ${project.architect.lastName}` : project.architect.email ? project.architect.email.split('@')[0] : 'Inredningsarkitekt'}
                                    role="Inredningsarkitekt"
                                    email={project.architect.email}
                                    company="Leadme AB"
                                    user={user}
                                    project={project}
                                    phone={project.architect.phone}
                                  />
                                )}
                                {project.additionalArchitects && project.additionalArchitects.length > 0 && (
                                  project.additionalArchitects.map(architect => (
                                    <TeamMember
                                      key={architect._id}
                                      entity={architect}
                                      name={architect.firstName && architect.lastName ? `${architect.firstName} ${architect.lastName}` : architect.email ? architect.email.split('@')[0] : 'Inredningsarkitekt'}
                                      role="Inredningsarkitekt"
                                      email={architect.email}
                                      company="Leadme AB"
                                      user={user}
                                      project={project}
                                      phone={architect.phone}
                                    />
                                  ))
                                )}
                              </div>
                            </div>
                          )}

                          {/* Byggare */}
                          <div>
                            <div className="text-sm font-medium text-gray-600 mb-2">Byggare</div>
                            <div className="space-y-3">
                              {project.builder && (
                                project.builderContactPerson ? (
                                  <TeamMember
                                    entity={{ ...project.builderContactPerson, builder: project.builder }}
                                    name={project.builderContactPerson.firstName && project.builderContactPerson.lastName ? `${project.builderContactPerson.firstName} ${project.builderContactPerson.lastName}` : project.builderContactPerson.email ? project.builderContactPerson.email.split('@')[0] : 'Byggare'}
                                    role="Byggare"
                                    email={project.builderContactPerson.email}
                                    company={project.builder.companyName}
                                    user={user}
                                    project={project}
                                    phone={project.builderContactPerson?.phone}
                                  />
                                ) : project.builder.contactPerson && (
                                  <TeamMember
                                    entity={project.builder.contactPerson}
                                    name={project.builder.contactPerson.firstName && project.builder.contactPerson.lastName ? `${project.builder.contactPerson.firstName} ${project.builder.contactPerson.lastName}` : project.builder.contactPerson.email ? project.builder.contactPerson.email.split('@')[0] : 'Byggare'}
                                    role="Byggare"
                                    email={project.builder.contactPerson.email}
                                    company={project.builder.companyName}
                                    user={user}
                                    project={project}
                                    phone={project.builder.contactPerson?.phone}
                                  />
                                )
                              )}
                            </div>
                          </div>

                          {/* Materialleverantörer */}
                          {project.materialSuppliers && project.materialSuppliers.length > 0 && (
                            <div className="pt-3 border-t border-gray-100">
                              <div className="text-sm font-medium text-gray-600 mb-2">Materialleverantörer</div>
                              <div className="space-y-3">
                                {project.materialSuppliers.map((materialSupplier, index) => (
                                  <TeamMember
                                    key={index}
                                    entity={materialSupplier.supplier}
                                    name={materialSupplier.contactPerson.firstName && materialSupplier.contactPerson.lastName ? `${materialSupplier.contactPerson.firstName} ${materialSupplier.contactPerson.lastName}` : 'Materialleverantör'}
                                    role="Materialleverantör"
                                    email={materialSupplier.contactPerson.email}
                                    company={materialSupplier.supplier.companyName}
                                    user={user}
                                    project={project}
                                    phone={materialSupplier.contactPerson.phone}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Underleverantörer */}
                          {project.subcontractors && project.subcontractors.length > 0 && (
                            <div className="pt-3 border-t border-gray-100">
                              <div className="text-sm font-medium text-gray-600 mb-2">Underleverantörer</div>
                              <div className="space-y-3">
                                {project.subcontractors.map((ue, index) => (
                                  <TeamMember
                                    key={`ue-${ue._id || index}`}
                                    entity={ue}
                                    name={ue.firstName && ue.lastName ? `${ue.firstName} ${ue.lastName}` : 'Underleverantör'}
                                    role={`Underleverantör${ue.contractorType ? ` (${ue.contractorType})` : ''}`}
                                    company={ue.companyName}
                                    user={user}
                                    project={project}
                                    hideContactInfo
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Projektörer */}
                          {project.projectors && project.projectors.length > 0 && (
                            <div className="pt-3 border-t border-gray-100">
                              <div className="text-sm font-medium text-gray-600 mb-2">Projektörer</div>
                              <div className="space-y-3">
                                {project.projectors.map((p, index) => (
                                  <TeamMember
                                    key={`projector-${p._id || index}`}
                                    entity={p}
                                    name={p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : p.email ? p.email.split('@')[0] : 'Projektör'}
                                    role={`Projektör${p.projectorType ? ` (${p.projectorType})` : ''}`}
                                    email={p.email}
                                    company={p.companyName}
                                    user={user}
                                    project={project}
                                    phone={p.phone}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                  </div>

                  {/* Rad 2: Möten, Att göra och Projektdokument */}
                  {user?.role === 'kund' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* Möten - expanderbar */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleSection(project._id, 'moten')}
                          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <span className="font-semibold text-gray-900">Möten</span>
                          </div>
                          <svg
                            className={`w-5 h-5 text-gray-500 transition-transform ${isSectionExpanded(project._id, 'moten') ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isSectionExpanded(project._id, 'moten') && (
                          <div className="px-4 py-3 bg-white">
                            <CustomerMeetingsSection
                              customerId={user._id}
                              projectId={project._id}
                              project={project}
                            />
                          </div>
                        )}
                      </div>

                      {/* Att göra - expanderbar */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleSection(project._id, 'attgora')}
                          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-leadme-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                              </svg>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">Att göra</span>
                              {myActionPoints.filter(ap => !ap.completed).length > 0 && (
                                <span className="px-2 py-0.5 bg-leadme-pink text-white text-xs font-medium rounded-full">
                                  {myActionPoints.filter(ap => !ap.completed).length}
                                </span>
                              )}
                            </div>
                          </div>
                          <svg
                            className={`w-5 h-5 text-gray-500 transition-transform ${isSectionExpanded(project._id, 'attgora') ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isSectionExpanded(project._id, 'attgora') && (
                          <div className="px-4 py-3 bg-white">
                            {loadingActionPoints ? (
                              <div className="flex items-center justify-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-leadme-pink"></div>
                              </div>
                            ) : myActionPoints.length > 0 ? (
                              <div className="space-y-2">
                                {[...myActionPoints]
                                  .sort((a, b) => {
                                    // Sortering: 1) Egna ej klara, 2) Protokoll ej klara, 3) Alla klara
                                    const aCompleted = a.completed ? 1 : 0;
                                    const bCompleted = b.completed ? 1 : 0;
                                    if (aCompleted !== bCompleted) return aCompleted - bCompleted;

                                    // Bland ej klara: egna först
                                    const aCustom = a.isCustom ? 0 : 1;
                                    const bCustom = b.isCustom ? 0 : 1;
                                    if (aCustom !== bCustom) return aCustom - bCustom;

                                    // Inom samma kategori: nyaste först
                                    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                                  })
                                  .map((ap) => (
                                  <div
                                    key={ap._id}
                                    className={`p-3 rounded-lg border ${
                                      ap.completed ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:border-leadme-pink'
                                    } transition-colors`}
                                  >
                                    {editingActionPoint === ap._id ? (
                                      <div className="space-y-2">
                                        <input
                                          type="text"
                                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                                          value={editFormData.title}
                                          onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
                                          placeholder="Rubrik"
                                        />
                                        <textarea
                                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-transparent resize-none"
                                          value={editFormData.content}
                                          onChange={(e) => setEditFormData(prev => ({ ...prev, content: e.target.value }))}
                                          placeholder="Beskrivning"
                                          rows={2}
                                        />
                                        <div className="flex items-center gap-2">
                                          <label className="text-xs text-gray-600">Deadline:</label>
                                          <input
                                            type="date"
                                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                                            value={editFormData.dueDate}
                                            onChange={(e) => setEditFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                                          />
                                        </div>
                                        <div className="flex gap-2 justify-end">
                                          <button
                                            onClick={cancelEditingActionPoint}
                                            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                                          >
                                            Avbryt
                                          </button>
                                          <button
                                            onClick={() => {
                                              if (ap.isCustom) {
                                                updateCustomTodo(ap._id, {
                                                  title: editFormData.title,
                                                  content: editFormData.content,
                                                  dueDate: editFormData.dueDate || null
                                                });
                                              } else {
                                                updateActionPoint(ap.protocolId, ap._id, {
                                                  title: editFormData.title,
                                                  content: editFormData.content,
                                                  dueDate: editFormData.dueDate || null
                                                });
                                              }
                                            }}
                                            className="px-2 py-1 text-xs bg-leadme-pink text-white rounded hover:bg-leadme-pink-dark"
                                          >
                                            Spara
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-start gap-2">
                                        <input
                                          type="checkbox"
                                          checked={ap.completed || false}
                                          onChange={(e) => {
                                            if (ap.isCustom) {
                                              toggleCustomTodo(ap._id, e.target.checked);
                                            } else {
                                              toggleActionPoint(ap.protocolId, ap._id, e.target.checked);
                                            }
                                          }}
                                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink cursor-pointer"
                                        />
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-start justify-between gap-2">
                                            <p className={`text-sm font-medium ${ap.completed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                                              {ap.title}
                                            </p>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                              <button
                                                onClick={() => startEditingActionPoint(ap)}
                                                className="text-gray-400 hover:text-leadme-pink"
                                                title="Redigera"
                                              >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                </svg>
                                              </button>
                                              {ap.isCustom && (
                                                <button
                                                  onClick={() => deleteCustomTodo(ap._id)}
                                                  className="text-gray-400 hover:text-red-500"
                                                  title="Ta bort"
                                                >
                                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                  </svg>
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                          {ap.content && (
                                            <p className={`text-xs mt-0.5 ${ap.completed ? 'text-gray-400' : 'text-gray-600'}`}>
                                              {ap.content}
                                            </p>
                                          )}
                                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            {ap.dueDate && (
                                              <span className={`text-xs ${
                                                ap.completed
                                                  ? 'text-gray-400'
                                                  : new Date(ap.dueDate) < new Date()
                                                    ? 'text-red-600 font-medium'
                                                    : 'text-gray-500'
                                              }`}>
                                                {new Date(ap.dueDate).toLocaleDateString('sv-SE')}
                                                {!ap.completed && new Date(ap.dueDate) < new Date() && ' (försenad)'}
                                              </span>
                                            )}
                                            {ap.sourceKanbanTaskId && ap.sourceKanbanCreatedByRole && ap.sourceKanbanCreatedByRole !== user?.role?.toLowerCase() ? (
                                              <span className="text-xs text-purple-600">
                                                Från {ap.sourceKanbanCreatedByRole}
                                              </span>
                                            ) : ap.isCustom ? (
                                              <span className="text-xs text-gray-400 italic">egen punkt</span>
                                            ) : (ap.meetingDisplayName || ap.protocolTitle) && (
                                              <span className="text-xs text-gray-400">
                                                från {ap.meetingDisplayName || ap.protocolTitle}
                                              </span>
                                            )}
                                            {ap.sourceNote && (
                                              <span className="text-xs text-teal-600">{ap.sourceNote}</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm py-2">Inga uppgifter just nu.</p>
                            )}

                            {/* Lägg till egen todo-punkt */}
                            {isAddingTodo ? (
                              <div className="mt-3 p-3 border border-leadme-pink rounded-lg bg-pink-50">
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                                    value={newTodoData.title}
                                    onChange={(e) => setNewTodoData(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="Vad behöver göras?"
                                    autoFocus
                                  />
                                  <textarea
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-transparent resize-none"
                                    value={newTodoData.content}
                                    onChange={(e) => setNewTodoData(prev => ({ ...prev, content: e.target.value }))}
                                    placeholder="Beskrivning (valfritt)"
                                    rows={2}
                                  />
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-600">Deadline:</label>
                                    <input
                                      type="date"
                                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                                      value={newTodoData.dueDate}
                                      onChange={(e) => setNewTodoData(prev => ({ ...prev, dueDate: e.target.value }))}
                                    />
                                  </div>
                                  <div className="flex gap-2 justify-end">
                                    <button
                                      onClick={() => {
                                        setIsAddingTodo(false);
                                        setNewTodoData({ title: '', content: '', dueDate: '' });
                                      }}
                                      className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                                    >
                                      Avbryt
                                    </button>
                                    <button
                                      onClick={() => addCustomTodo(project._id)}
                                      disabled={!newTodoData.title.trim()}
                                      className="px-2 py-1 text-xs bg-leadme-pink text-white rounded hover:bg-leadme-pink-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      Lägg till
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setIsAddingTodo(true)}
                                className="mt-3 w-full py-2 text-sm text-gray-500 hover:text-leadme-pink border border-dashed border-gray-300 hover:border-leadme-pink rounded-lg transition-colors flex items-center justify-center gap-1"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                                Lägg till egen punkt
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Projektdokument - expanderbar */}
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleSection(project._id, 'dokument')}
                          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <span className="font-semibold text-gray-900">Projektdokument</span>
                          </div>
                          <svg
                            className={`w-5 h-5 text-gray-500 transition-transform ${isSectionExpanded(project._id, 'dokument') ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isSectionExpanded(project._id, 'dokument') && (
                          <div className="px-4 py-3 bg-white space-y-2">
                            {/* Offert — godkänd + eventuell ny version + tilläggsofferter */}
                            {user?.role === 'kund' && (() => {
                              /**
                               * ORIGINALSPÅRET filtreras ut först. Utan det
                               * kunde ett godkänt tillägg bli "den godkända
                               * offerten" i kundens vy, eftersom `find` tar
                               * första träffen och det nu finns flera.
                               */
                              const allaOfferter = Array.isArray(offerter) ? offerter : [];
                              const originalspar = allaOfferter.filter(arOriginal);
                              const acceptedOffert = originalspar
                                .find(o => o.status === 'accepted' || o.previousStatus === 'accepted') || null;
                              const pendingOffert = originalspar
                                .find(o => ['sent', 'viewed'].includes(o.status)) || null;

                              /**
                               * ETT KORT PER EXTRA SPÅR.
                               *
                               * `find` räckte inte: finns Tilläggsoffert 1 och 2
                               * visades bara den ena, och när tillägget blivit
                               * godkänt försvann det helt eftersom det gröna
                               * kortet redan var upptaget av originalofferten.
                               *
                               * Inom spåret gäller den godkända, annars den
                               * högsta icke-obsoleta versionen. 'commented' är
                               * med — kunden som lämnat en kommentar ska inte se
                               * kortet försvinna medan hen väntar på svar.
                               * Originalspårets statusurval rörs INTE.
                               */
                              const extraPerSpar = new Map();
                              allaOfferter
                                .filter(o => arExtra(o) && o.status !== 'obsolete')
                                .forEach(o => {
                                  const befintlig = extraPerSpar.get(o.extraNummer);
                                  const arGodkand = x => x.status === 'accepted' || x.previousStatus === 'accepted';
                                  if (!befintlig) return extraPerSpar.set(o.extraNummer, o);
                                  if (arGodkand(o) && !arGodkand(befintlig)) return extraPerSpar.set(o.extraNummer, o);
                                  if (arGodkand(befintlig)) return;
                                  if ((parseFloat(o.version) || 0) > (parseFloat(befintlig.version) || 0)) {
                                    extraPerSpar.set(o.extraNummer, o);
                                  }
                                });
                              const extraOfferter = [...extraPerSpar.values()]
                                .sort((a, b) => (a.extraNummer || 0) - (b.extraNummer || 0));

                              if (!acceptedOffert && !pendingOffert && extraOfferter.length === 0) return null;
                              return (
                                <>
                                  {/* Ny offert att granska */}
                                  {pendingOffert && (
                                    <div
                                      onClick={() => {
                                        setSelectedOffert(pendingOffert);
                                        setShowOffertModal(true);
                                      }}
                                      className="bg-blue-50 border border-blue-200 p-4 rounded-lg cursor-pointer hover:shadow-md transition-all duration-200"
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                          <span className="relative flex h-2.5 w-2.5">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500"></span>
                                          </span>
                                          <h4 className="font-medium text-gray-900">Ny offert</h4>
                                        </div>
                                        <span className="px-2 py-1 rounded-full text-xs text-blue-700 bg-white font-medium">
                                          Att granska
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {pendingOffert.builderId?.companyName || 'Byggföretag'} - Version {pendingOffert.version || '1.0'}
                                      </p>
                                    </div>
                                  )}
                                  {/* Godkänd offert */}
                                  {acceptedOffert && (
                                    <div
                                      onClick={() => {
                                        setSelectedOffert(acceptedOffert);
                                        setShowOffertModal(true);
                                      }}
                                      className="bg-green-50 p-4 rounded-lg cursor-pointer hover:shadow-md transition-all duration-200"
                                    >
                                      <div className="flex justify-between items-center">
                                        <h4 className="font-medium text-gray-900">Offert</h4>
                                        <span className="px-2 py-1 rounded-full text-xs text-green-700 bg-white">
                                          Godkänd
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {acceptedOffert.builderId?.companyName || 'Byggföretag'} - Version {acceptedOffert.version || '1.0'}
                                      </p>
                                    </div>
                                  )}
                                  {/* Tilläggsofferter — tillägg under produktion.
                                      Egna kort, vid sidan av originalet, så det
                                      syns att den godkända offerten står kvar. */}
                                  {extraOfferter.map(extra => {
                                    const arGodkand = extra.status === 'accepted' || extra.previousStatus === 'accepted';
                                    const arNekad = extra.status === 'declined';
                                    return (
                                      <div
                                        key={extra._id}
                                        onClick={() => {
                                          setSelectedOffert(extra);
                                          setShowOffertModal(true);
                                        }}
                                        className={`p-4 rounded-lg cursor-pointer hover:shadow-md transition-all duration-200 border ${
                                          arGodkand
                                            ? 'bg-green-50 border-green-200'
                                            : arNekad
                                              ? 'bg-gray-50 border-gray-200'
                                              : 'bg-amber-50 border-amber-200'
                                        }`}
                                      >
                                        <div className="flex justify-between items-center">
                                          <div className="flex items-center gap-2">
                                            {!arGodkand && !arNekad && (
                                              <span className="relative flex h-2.5 w-2.5">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                                              </span>
                                            )}
                                            <h4 className="font-medium text-gray-900">
                                              {visaBeteckning(extra)}
                                            </h4>
                                          </div>
                                          <span className={`px-2 py-1 rounded-full text-xs bg-white font-medium ${
                                            arGodkand ? 'text-green-700' : arNekad ? 'text-gray-600' : 'text-amber-700'
                                          }`}>
                                            {arGodkand ? 'Godkänd' : arNekad ? 'Avböjd' : 'Att granska'}
                                          </span>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                          {extra.builderId?.companyName || 'Byggföretag'} · Tillkommande arbete
                                        </p>
                                      </div>
                                    );
                                  })}
                                </>
                              );
                            })()}
                            <DocumentCard
                              title="Produktval"
                              status={project.shoppingList?.status || 'Saknas'}
                              progress={calculateProgress(project.shoppingList, 'Produktval')}
                              onClick={() => {
                                if (project.shoppingList?.status?.toLowerCase() !== 'saknas') {
                                  navigate(`/myproject/${project._id}/produktval`);
                                }
                              }}
                              documentType="produktval"
                            />
                            <DocumentCard
                              title="Rumsbeskrivning"
                              status={project.rumsa?.status || 'Saknas'}
                              progress={calculateProgress(project.rumsa, 'Rumsbeskrivning')}
                              onClick={() => {
                                if (project.rumsa?.status?.toLowerCase() !== 'saknas') {
                                  navigate(`/myproject/${project._id}/rumsbeskrivning`);
                                }
                              }}
                              documentType="rumsbeskrivning"
                            />
                            <DocumentCard
                              title="Tidplan"
                              status={project.timeline?.status || 'missing'}
                              progress={calculateProgress(project.timeline, 'Tidplan')}
                              onClick={() => {
                                if (project.timeline?.status?.toLowerCase() !== 'missing') {
                                  navigate(`/myproject/${project._id}/timeline`);
                                }
                              }}
                              documentType="tidplan"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        ))}

        {/* Offerter-sektion - visa bara för kunder UTAN projekt när det finns skickade offerter */}
        {/* När kunden har ett projekt visas offerten i projektöversikten istället */}
        {(() => {
          const isCustomer = user?.role === 'kund';
          const hasProjects = projects && projects.length > 0;
          const isArray = Array.isArray(offerter);
          const hasSentOffers = isArray && offerter.some(offer =>
            ['sent', 'viewed', 'accepted', 'declined', 'commented'].includes(offer.status)
          );

          // Visa INTE denna sektion om kunden har ett projekt (offerten visas då i projektöversikten)
          return isCustomer && !hasProjects && isArray && hasSentOffers;
        })() && (
          <>
            {/* Meddelande från byggkonsult vid offertskick — visas ovanför Mina Offerter */}
            {(() => {
              const msg = Array.isArray(offerter)
                ? offerter.map(o => o?.procurementId?.offertMessage).find(m => m && m.text)
                : null;
              if (!msg?.text) return null;
              return (
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white mt-6">
                  <button
                    onClick={() => setExpandedOffertMessage(!expandedOffertMessage)}
                    className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 3v-3z" />
                        </svg>
                      </div>
                      <div className="min-w-0 text-left">
                        <span className="font-semibold text-gray-900">Meddelande från Leadme</span>
                        <p className="text-xs sm:text-sm text-gray-500">Information inför att du svarar på offerten</p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-400 transform transition-transform ${expandedOffertMessage ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedOffertMessage && (
                    <div className="border-t border-gray-200 p-4 sm:p-6">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{msg.text}</p>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white mt-6">
              {/* Header med expand/collapse funktionalitet */}
              <button
                onClick={() => setExpandedOfferter(!expandedOfferter)}
                className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <div className="min-w-0 text-left">
                    <span className="font-semibold text-gray-900">Mina Offerter</span>
                    <p className="text-xs sm:text-sm text-gray-500">Offerter från byggare</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(() => {
                    if (offerterLoading) {
                      return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-pink-400"></div>;
                    }
                    const offerterCount = Array.isArray(offerter) ? offerter.length : 0;
                    const newOfferter = Array.isArray(offerter) ? offerter.filter(o => o.status === 'sent').length : 0;
                    return (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-pink-100 text-pink-700">
                          {offerterCount} totalt
                        </span>
                        {newOfferter > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-yellow-100 text-yellow-700">
                            {newOfferter} nya
                          </span>
                        )}
                      </>
                    );
                  })()}
                  <svg
                    className={`w-5 h-5 text-gray-400 transform transition-transform ${
                      expandedOfferter ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

            {expandedOfferter && (
              <div className="border-t border-gray-200">
                <div className="p-6">
                  
                  {/* Bokat Offertuppföljningsmöte - accordion sektion */}
                  {(() => {
                    const followupRequests = procurement?.offertFollowupRequests || offertFollowupRequests || [];
                    const latestFollowupRequest = getActiveOffertFollowupRequest(followupRequests);
                    const hasBookedOffertFollowup = latestFollowupRequest?.status === 'booked' && latestFollowupRequest?.consultantResponse?.acceptedSlot;
                    
                    // Kontrollera om det finns en godkänd offert
                    const hasAcceptedOffer = Array.isArray(offerter) && offerter.some(offer => offer.status === 'accepted');
                    
                    // Visa bara om möte är bokat OCH ingen offert är godkänd
                    if (!hasBookedOffertFollowup || hasAcceptedOffer) return null;
                    
                    const acceptedSlot = latestFollowupRequest.consultantResponse.acceptedSlot;
                    const teamsJoinUrl = latestFollowupRequest.consultantResponse.teamsJoinUrl;
                    
                    return (
                      <div className="mb-6 bg-white rounded-lg shadow-sm border border-green-200">
                        {/* Accordion header - klickbar */}
                        <div 
                          className="flex items-center justify-between p-4 cursor-pointer select-none bg-green-50 hover:bg-green-100 transition-colors rounded-t-lg"
                          onClick={() => setExpandedBookedOffertFollowup(!expandedBookedOffertFollowup)}
                          role="button"
                          tabIndex={0}
                        >
                          <h3 className="text-base font-semibold text-green-800">
                            📅 Offertuppföljningsmöte bokat
                          </h3>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-green-700 font-medium">
                              {acceptedSlot}
                            </span>
                            <svg 
                              className={`w-5 h-5 text-green-600 transition-transform duration-200 ${expandedBookedOffertFollowup ? 'rotate-180' : ''}`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        
                        {/* Accordion content - expanderbar */}
                        {expandedBookedOffertFollowup && (
                          <div className="p-4 bg-green-50 border-t border-green-200 rounded-b-lg">
                            <div className="bg-white border border-green-200 rounded-lg p-4">
                              <p className="text-sm text-gray-600 mb-2">
                                Möte bokat för:
                              </p>
                              <div className="text-base font-semibold text-green-900 mb-3">
                                {acceptedSlot}
                              </div>
                              
                              {teamsJoinUrl && (
                                <div className="mb-3">
                                  <a
                                    href={teamsJoinUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    Gå med i Teams-möte
                                  </a>
                                </div>
                              )}
                              
                              <p className="text-xs text-green-600">
                                ✉️ Kalenderinbjudan har skickats till er via e-post.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* 🔥 Toggle för historiska offerter */}
                  {hiddenObsoleteCount > 0 && !showObsoleteOffers && (
                    <div className="mb-4 flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                      <div className="flex items-center gap-2 text-gray-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm">
                          {hiddenObsoleteCount} {hiddenObsoleteCount === 1 ? 'tidigare version dold' : 'tidigare versioner dolda'}
                        </span>
                      </div>
                      <button
                        onClick={() => setShowObsoleteOffers(true)}
                        className="text-sm text-leadme-pink hover:text-leadme-pink-dark font-medium flex items-center gap-1"
                      >
                        Visa historiska offerter
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {showObsoleteOffers && (
                    <div className="mb-4 flex items-center justify-between bg-amber-50 rounded-lg px-4 py-3 border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-700">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium">Visar alla versioner inklusive historiska</span>
                      </div>
                      <button
                        onClick={() => setShowObsoleteOffers(false)}
                        className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center gap-1"
                      >
                        Dölj historiska
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Kort för varje offert */}
                  {(() => {
                    // Inkludera alla relevanta statusar inklusive 'obsolete' (för att visa äldre versioner)
                    const allOffers = Array.isArray(offerter) ? offerter.filter(offer =>
                      ['sent', 'viewed', 'accepted', 'declined', 'commented', 'obsolete'].includes(offer.status)
                    ) : [];

                    // Hjälpfunktion: kolla om en offert är/har varit godkänd
                    const isOfferAccepted = (offer) => offer.status === 'accepted' || offer.previousStatus === 'accepted';

                    // Kontrollera om någon offert är/har varit godkänd.
                    // ORIGINALSPÅRET avgör — ett godkänt tillägg betyder inte att
                    // affären är vunnen, och skulle annars peka om
                    // `acceptedBuilderId` och styra filtreringen nedan.
                    const acceptedOffer = allOffers.filter(arOriginal).find(isOfferAccepted);
                    const hasAcceptedOffer = Boolean(acceptedOffer);

                    // Om det finns en godkänd offert, hämta byggföretagets ID
                    const acceptedBuilderId = acceptedOffer?.builderId?._id || acceptedOffer?.builderId;

                    // 🔥 Filtrera offerter baserat på showObsoleteOffers toggle
                    // - Om showObsoleteOffers är true: visa ALLA offerter (backend har redan filtrerat)
                    // - Om ingen är godkänd och toggle är av: visa alla utom obsolete
                    // - Om en är godkänd: visa alla versioner från det godkända byggföretaget
                    let visibleOffers;
                    if (showObsoleteOffers) {
                      // Visa alla (backend har redan skickat alla)
                      visibleOffers = allOffers;
                    } else if (hasAcceptedOffer) {
                      // Visa alla versioner från godkänt byggföretag — plus varje
                      // tillägg. Ett tillägg hör till kunden oavsett vilken
                      // byggare som prisat det, och får inte filtreras bort av en
                      // regel som handlar om vem som vann upphandlingen.
                      visibleOffers = allOffers.filter(offer => {
                        if (arExtra(offer)) return true;
                        const offertBuilderId = offer.builderId?._id || offer.builderId;
                        return offertBuilderId === acceptedBuilderId;
                      });
                    } else {
                      // Visa endast aktiva offerter (inte obsolete)
                      visibleOffers = allOffers.filter(offer => offer.status !== 'obsolete');
                    }
                    
                    if (visibleOffers.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <div className="text-gray-400 mb-4">
                            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">Inga offerter ännu</h4>
                          <p className="text-gray-600">
                            Du har inga offerter att visa. Offerter kommer att visas här när byggkonsulten skickar dem till dig.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {visibleOffers.map((offert) => (
                          <OffertCard
                            key={offert._id}
                            offert={offert}
                            hasAcceptedOffer={hasAcceptedOffer}
                            acceptedBuilderId={acceptedBuilderId}
                            onClick={() => {
                              // Tillåt klick för alla synliga offerter (filtrerade redan till samma byggföretag om godkänd finns)
                              setSelectedOffert(offert);
                              setShowOffertModal(true);
                            }}
                          />
                        ))}
                      </div>
                    );
                  })()}
                  
                  {/* Jämför offerter sektion */}
                  {(() => {
                    // Inkludera ALLA offerter (även obsolete) för jämförelse.
                    //
                    // Tilläggsofferter hålls UTANFÖR. Jämförelsen finns för att
                    // ställa byggare och versioner av samma jobb mot varandra —
                    // pris, arbetsbeskrivning, villkor. Ett tillägg är ett annat
                    // jobb, och sida vid sida med den sålda offerten säger
                    // prisskillnaden ingenting.
                    const allOffersForComparison = Array.isArray(offerter) ? offerter.filter(offer =>
                      arOriginal(offer) &&
                      ['sent', 'viewed', 'accepted', 'declined', 'commented', 'obsolete'].includes(offer.status)
                    ) : [];
                    
                    // Hjälpfunktion: kolla om en offert är/har varit godkänd
                    const isOfferAccepted = (offer) => offer.status === 'accepted' || offer.previousStatus === 'accepted';
                    
                    // Kontrollera om någon offert är/har varit godkänd
                    const acceptedOffer = allOffersForComparison.find(isOfferAccepted);
                    const hasAcceptedOffer = Boolean(acceptedOffer);
                    const acceptedBuilderId = acceptedOffer?.builderId?._id || acceptedOffer?.builderId;
                    
                    // Filtrera offerter för jämförelse:
                    // - Om en offert är godkänd: visa alla versioner från det godkända byggföretaget
                    // - Om showObsoleteOffers är true: visa alla offerter
                    // - Annars: visa alla utom obsolete
                    const comparableOffers = hasAcceptedOffer
                      ? allOffersForComparison.filter(offer => {
                          const offertBuilderId = offer.builderId?._id || offer.builderId;
                          return offertBuilderId === acceptedBuilderId;
                        })
                      : showObsoleteOffers
                        ? allOffersForComparison
                        : allOffersForComparison.filter(offer => offer.status !== 'obsolete');
                    
                    // Visa jämförelse om det finns minst 2 jämförbara offerter
                    return comparableOffers.length >= 2 && (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="text-center">
                          <div className="mb-4">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Jämför offerter</h3>
                            <p className="text-sm text-gray-600 max-w-md mx-auto">
                              Du kan jämföra flera offerter sida vid sida för att enkelt se skillnader i pris, 
                              arbetsbeskrivning och villkor. Välj 2-3 offerter att jämföra.
                            </p>
                          </div>
                          <button
                            onClick={() => setShowCompareModal(true)}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark transition-colors font-medium"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
                            </svg>
                            Jämför offerter
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
            </div>
          </>
        )}

        {/* Offertuppföljningsmöte-sektion för kunder - ENDAST om de inte har ett projekt ännu */}
        {(() => {
          const isCustomer = user?.role === 'kund';
          const isArray = Array.isArray(offerter);
          const hasSentOffers = isArray && offerter.some(offer => 
            ['sent', 'viewed', 'accepted', 'declined', 'commented'].includes(offer.status)
          );
          
          // Kontrollera om det finns en godkänd offert
          const hasAcceptedOffer = isArray && offerter.some(offer => offer.status === 'accepted');
          
          // Kontrollera om offertuppföljningsmöte redan är bokat
          const followupRequests = procurement?.offertFollowupRequests || offertFollowupRequests || [];
          const latestFollowupRequest = getActiveOffertFollowupRequest(followupRequests);
          const hasBookedOffertFollowup = latestFollowupRequest?.status === 'booked' && latestFollowupRequest?.consultantResponse?.acceptedSlot;
          
          // Kontrollera om kunden redan har ett projekt
          const hasProject = projects.length > 0;
          
          // Visa bara om kund har offerter men INTE har bokat uppföljningsmöte OCH ingen offert är godkänd OCH inget projekt finns
          return isCustomer && isArray && hasSentOffers && !hasBookedOffertFollowup && !hasAcceptedOffer && !hasProject;
        })() && (
          <div className="border border-gray-200 rounded-xl overflow-hidden bg-white mt-6 mb-6">
            <button
              onClick={() => setIsOffertFollowupExpanded(!isOffertFollowupExpanded)}
              className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="min-w-0 text-left">
                  <span className="font-semibold text-gray-900">Offertuppföljningsmöte</span>
                  <p className="text-xs sm:text-sm text-gray-500">Diskutera offerterna med din byggkonsult</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(() => {
                  const latestRequest = getActiveOffertFollowupRequest(offertFollowupRequests);
                  if (latestRequest?.status === 'booked') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-700">Bokat</span>;
                  if (latestRequest?.status === 'accepted') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-700">Accepterat</span>;
                  if (latestRequest?.status === 'pending') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-yellow-100 text-yellow-700">Väntar</span>;
                  if (latestRequest?.status === 'new_proposal_sent') return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-700">Nya förslag</span>;
                  return null;
                })()}
                <svg
                  className={`w-5 h-5 text-gray-400 transform transition-transform ${
                    isOffertFollowupExpanded ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            {isOffertFollowupExpanded && (
              <div className="border-t border-gray-200 p-4 sm:p-6">
                  {(() => {
                    // Hitta aktuell förfrågan (aktiv prioriteras över historisk bokad)
                    const latestRequest = getActiveOffertFollowupRequest(offertFollowupRequests);

                    // Visa bokat möte
                    if (latestRequest?.status === 'booked' && latestRequest?.consultantResponse?.acceptedSlot) {
                      return (
                        <div className="space-y-3">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center">
                              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm font-medium text-green-800">Möte bokat</span>
                            </div>
                            <p className="text-sm text-green-700 mt-1">
                              {latestRequest.consultantResponse.acceptedSlot}
                            </p>
                            <p className="text-sm text-green-600 mt-2">
                              Kalenderinbjudan har skickats via e-post.
                            </p>
                          </div>
                        </div>
                      );
                    }

                    // Visa accepterat förslag (väntar på att byggkonsult bokar)
                    if (latestRequest?.status === 'accepted' && latestRequest?.consultantResponse?.acceptedSlot) {
                      return (
                        <div className="space-y-3">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-center">
                              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-sm font-medium text-green-800">Tidsförslag accepterat</span>
                            </div>
                            <p className="text-sm text-green-700 mt-1">
                              {latestRequest.consultantResponse.acceptedSlot}
                            </p>
                            <p className="text-sm text-green-600 mt-2">
                              Din byggkonsult kommer att boka mötet och skicka kalenderinbjudan.
                            </p>
                          </div>
                        </div>
                      );
                    }

                    // Visa nya förslag från byggkonsult
                    if (latestRequest?.status === 'new_proposal_sent' && latestRequest?.consultantResponse?.newProposals) {
                      return (
                        <div className="space-y-3">
                          <p className="text-gray-600 text-sm">
                            Din byggkonsult har föreslagit nya tider för offertuppföljningsmötet:
                          </p>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-blue-900 mb-2">Föreslagna tider:</h4>
                            <div className="space-y-2">
                              {latestRequest.consultantResponse.newProposals.map((proposal, index) => (
                                <div key={index} className="flex items-center justify-between bg-white rounded p-2 border">
                                  <span className="text-sm text-gray-900">{proposal.displayText}</span>
                                  <button
                                    onClick={() => handleAcceptOffertFollowupProposal(latestRequest._id, proposal)}
                                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                  >
                                    Acceptera
                                  </button>
                                </div>
                              ))}
                            </div>
                            {latestRequest.consultantResponse.message && (
                              <div className="mt-3 pt-3 border-t border-blue-200">
                                <p className="text-sm text-blue-800">
                                  <span className="font-medium">Meddelande:</span> {latestRequest.consultantResponse.message}
                                </p>
                              </div>
                            )}
                            <div className="mt-3 pt-3 border-t border-blue-200 flex gap-2">
                              <button
                                onClick={() => setShowOffertFollowupModal(true)}
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                Föreslå andra tider
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // Visa pending status - olika UI beroende på vem som föreslagit
                    if (latestRequest?.status === 'pending') {
                      // Kommer tiderna från kundsidan eller från byggkonsulten?
                      //
                      // Rollen avgör, inte vem som är inloggad. Jämförelsen med
                      // user._id ensam gjorde att beställare 2 såg huvudkundens
                      // egna tider som byggkonsultens förslag och kunde acceptera
                      // dem — byggkonsulten fick då en notis om att kunden
                      // accepterat ett förslag hen aldrig skickat.
                      const requestedById = latestRequest.requestedBy?._id || latestRequest.requestedBy?.$oid || latestRequest.requestedBy;
                      const requestedByRole = latestRequest.requestedBy?.role;
                      const isCustomerRequest = requestedByRole
                        ? requestedByRole === 'kund'
                        : requestedById?.toString() === user?._id?.toString();

                      if (isCustomerRequest) {
                        // Kund har föreslagit - väntar på byggkonsult
                        return (
                          <div className="space-y-3">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                              <div className="flex items-center mb-2">
                                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-sm font-medium text-yellow-800">Väntar på svar från byggkonsult</span>
                              </div>
                              <div className="text-sm text-yellow-700">
                                <p className="font-medium mb-1">Dina föreslagna tider:</p>
                                <ul className="list-disc list-inside space-y-1">
                                  {latestRequest.meetingSlots.map((slot, index) => (
                                    <li key={index}>{slot}</li>
                                  ))}
                                </ul>
                                {latestRequest.notes && (
                                  <p className="mt-2">
                                    <span className="font-medium">Meddelande:</span> {latestRequest.notes}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      } else {
                        // Byggkonsult har föreslagit - kund kan acceptera eller föreslå andra tider
                        const slotsToShow = latestRequest.proposedSlots?.length > 0
                          ? latestRequest.proposedSlots
                          : latestRequest.meetingSlots || [];

                        return (
                          <div className="space-y-3">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-center mb-3">
                                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-sm font-medium text-blue-800">Föreslagna tider från byggkonsult</span>
                              </div>
                              <div className="text-sm text-blue-700 mb-4">
                                <p className="font-medium mb-2">Föreslagna tider:</p>
                                <div className="space-y-2">
                                  {slotsToShow.map((slot, index) => {
                                    const slotText = typeof slot === 'object' ? (slot.displayText || slot.text) : slot;
                                    return (
                                      <div key={index} className="flex items-center justify-between bg-white rounded p-3 border border-blue-200">
                                        <span className="font-medium">{slotText}</span>
                                        <button
                                          onClick={() => handleAcceptOffertFollowupProposal(latestRequest._id, typeof slot === 'object' ? { displayText: slotText, ...slot } : { displayText: slotText })}
                                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                        >
                                          Acceptera
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                                {latestRequest.notes && (
                                  <p className="mt-3 p-2 bg-blue-100 rounded">
                                    <span className="font-medium">Meddelande:</span> {latestRequest.notes}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => setShowOffertFollowupModal(true)}
                                className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
                              >
                                Föreslå andra tider
                              </button>
                            </div>
                          </div>
                        );
                      }
                    }

                    // Visa standard-knapp om inga förslag finns
                    return (
                      <div className="space-y-3">
                        <p className="text-gray-600 text-sm">
                          Vill du diskutera dina offerter med din byggkonsult? Föreslå tider för ett uppföljningsmöte där ni kan gå igenom alla offerter tillsammans och diskutera nästa steg.
                        </p>
                        <button
                          onClick={() => setShowOffertFollowupModal(true)}
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Föreslå mötestider
                        </button>
                      </div>
                    );
                  })()}
              </div>
            )}
          </div>
        )}

        {/* Underlag-sektion (BK-skiss PDF) — visas som egen sektion (under Offertuppföljningsmöte)
            när offert finns men kunden ännu INTE har ett projekt. Utan offert visas Underlag
            inuti estimatet i stället; i projekt-läge döljs den helt (kunden ser underlag via
            projektets egna vyer). Så kunden ser aldrig Underlag dubbelt. */}
        {user?.role === 'kund' && procurement && procurement._id && (offerter?.length > 0) && !(projects?.length > 0) && (
          /* Ramen döljs med CSS i stället för att villkoras bort, eftersom
             SkissSection måste hinna monteras och hämta filerna för att kunna
             svara på om mappen är tom. Villkorad rendering hade gjort att den
             aldrig kördes och sektionen alltid varit dold. */
          <div className={`border border-gray-200 rounded-xl overflow-hidden bg-white mt-6 ${harUnderlag ? '' : 'hidden'}`}>
            <button
              onClick={() => setExpandedUnderlag(!expandedUnderlag)}
              className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="min-w-0 text-left">
                  <span className="font-semibold text-gray-900">Underlag</span>
                  <p className="text-xs sm:text-sm text-gray-500">BK-skiss och övriga underlag</p>
                </div>
              </div>
              <svg
                className={`w-5 h-5 text-gray-400 transform transition-transform ${
                  expandedUnderlag ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div className={`border-t border-gray-200 p-4 sm:p-6 ${expandedUnderlag ? '' : 'hidden'}`}>
              <SkissSection
                procurementData={procurement}
                currentUser={user}
                onHarUnderlag={setHarUnderlag}
              />
            </div>
          </div>
        )}

        {/* Estimat-sektion för kunder - dölj om de har fått offert, såvida inte byggkonsult har aktiverat synlighet */}
        {user?.role === 'kund' && estimat.length > 0 && (() => {
          const isArray = Array.isArray(offerter);
          const hasSentOffers = isArray && offerter.some(offer => 
            ['sent', 'viewed', 'accepted', 'declined', 'commented'].includes(offer.status)
          );
          const latestEstimat = estimatSpar.length > 0 ? estimatSpar[0] : null;
          const showEstimatToCustomer = latestEstimat?.showEstimatToCustomer || false;
          
          // Visa estimat om:
          // 1. Inga offerter har skickats ELLER
          // 2. Byggkonsult har aktiverat synlighet för kund
          return !hasSentOffers || showEstimatToCustomer;
        })() && (
          <div className="border border-gray-200 rounded-xl bg-white mt-6">
            {(() => {
              const latest = valtEstimat;
              const estimatTitle = (() => {
                if (!latest) return 'Mitt Estimat';
                const ver = Number.isFinite(Number(latest.version)) ? Number(latest.version).toFixed(1) : '1.0';
                // Extranamnet skiljer parallella estimat åt — utan det ser två
                // poster identiska ut för kunden.
                const namn = (latest.variantName || '').trim();
                return namn ? `Estimat ${ver} ${namn}` : `Estimat ${ver}`;
              })();

              // Compute status badge
              const statusBadge = (() => {
                if (!latest) return null;
                const isApproved = (latest?.status === 'approved') || (latest?.customerResponse?.status === 'approved');
                const isDeclined = (latest?.status === 'declined') || (latest?.customerResponse?.status === 'declined');
                const hasFollowup = Boolean(latest?.estimatFollowup?.teamsJoinUrl || latest?.estimatFollowup?.meetingAt || latest?.estimatFollowup?.selectedSlot);
                const hasConsultantReplies = latest.byggkonsultResponses && Object.keys(latest.byggkonsultResponses).length > 0;
                // Bara feedback som gäller DEN HÄR versionen. Kommentarer ärvs
                // in i varje ny version, så utan tidskontrollen tändes badgen
                // direkt när ett nytt estimat skickades.
                const hasCustomerFeedback = hasPublishedCustomerCommentAfter(
                  latest.customerResponse?.comments,
                  latest.sentAt
                );

                if (isApproved) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-700">Godkänt</span>;
                if (isDeclined) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-red-100 text-red-700">Tackat nej</span>;
                if (hasFollowup) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-700">Uppföljning bokad</span>;
                if (hasConsultantReplies) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-700">Svar från BK</span>;
                if (hasCustomerFeedback) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-yellow-100 text-yellow-700">Feedback skickad</span>;
                if (latest?.version === 1 && latest?.sentAt) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-700">Nytt estimat</span>;
                return null;
              })();

              // Compute subtitle
              const subtitle = (() => {
                if (!latest) return 'Se ditt estimat och lämna feedback';
                const formatDate = (d) => new Date(d).toLocaleDateString('sv-SE');
                const dateToShow = latest.sentAt || latest.createdAt || latest.updatedAt;
                const label = latest.sentAt ? 'Skickad' : 'Datum';
                return `${label}: ${dateToShow ? formatDate(dateToShow) : ''}`;
              })();

              // Compute action buttons
              const actionButton = (() => {
                if (!latest) return null;
                const hasCustomerFeedback = latest?.customerResponse?.overallFeedback?.length > 0 || latest?.customerFeedback?.length > 0;
                const anyConsultantResponse = latest?.byggkonsultResponses && Object.keys(latest.byggkonsultResponses).length > 0;
                // Styr vilken knapp kunden erbjuds. Måste gälla DEN HÄR
                // versionen: med ärvda kommentarer räknades en helt ny version
                // som redan besvarad, och kunden fick aldrig "Ge feedback".
                const hasAnyCustomerComments = hasPublishedCustomerCommentAfter(
                  latest?.customerResponse?.comments,
                  latest?.sentAt
                );
                const isApproved = latest?.status === 'approved';
                const isApprovedByConsultant = latest?.approvedByConsultant;
                const isDeclined = latest?.status === 'declined';
                const hasSentFeedback = hasCustomerFeedback || hasAnyCustomerComments;
                const hasMeetingRequest = Boolean(latest?.customerResponse?.comments?.__meetingRequest__);
                const hasProposedTimes = Boolean(
                  latest?.estimatFollowup?.customerResponse?.proposals &&
                  latest.estimatFollowup.customerResponse.proposals.length > 0
                );
                const hasMeetingBooked = Boolean(
                  latest?.estimatFollowup?.teamsJoinUrl ||
                  latest?.estimatFollowup?.meetingAt ||
                  latest?.estimatFollowup?.selectedSlot
                );
                const hasGivenMeetingTimes = hasMeetingRequest || hasProposedTimes || hasMeetingBooked;
                const showFeedbackButton = !hasSentFeedback && !anyConsultantResponse && !isApproved && !isApprovedByConsultant;
                const showMeetingButton = hasSentFeedback && !hasGivenMeetingTimes && !isApproved && !isApprovedByConsultant && !isDeclined;
                const showApproveButton = hasMeetingBooked && !isApproved && !isApprovedByConsultant && !isDeclined;

                if (showFeedbackButton) {
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!expandedEstimat) setExpandedEstimat(true);
                        setTimeout(() => {
                          const feedbackSection = document.getElementById('feedback-section');
                          if (feedbackSection) feedbackSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                      }}
                      className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-leadme-pink text-white text-[10px] sm:text-xs font-medium rounded-lg hover:bg-leadme-pink-dark transition-colors whitespace-nowrap"
                    >
                      Svara
                    </button>
                  );
                }
                if (showMeetingButton) {
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEstimatMeetingSlots([]);
                        setShowEstimatMeetingModal(true);
                      }}
                      className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-600 text-white text-[10px] sm:text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                    >
                      Boka genomgång
                    </button>
                  );
                }
                if (showApproveButton) {
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!expandedEstimat) setExpandedEstimat(true);
                        setTimeout(() => {
                          const nastaStegSection = document.getElementById('nasta-steg-section');
                          if (nastaStegSection) nastaStegSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                      }}
                      className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-green-600 text-white text-[10px] sm:text-xs font-medium rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                    >
                      Nästa steg
                    </button>
                  );
                }
                return null;
              })();

              return (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={expandedEstimat ? 'Minimera Mitt Estimat' : 'Expandera Mitt Estimat'}
                  onClick={() => setExpandedEstimat(!expandedEstimat)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedEstimat(!expandedEstimat); } }}
                  className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="min-w-0 text-left">
                        <span className="font-semibold text-gray-900">{estimatTitle}</span>
                        <p className="text-xs sm:text-sm text-gray-500">{subtitle}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Desktop: visa action-knapp inline */}
                      <span className="hidden sm:inline-flex">{actionButton}</span>
                      {latest?.showForstudiePresentationToCustomer && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              `/forstudie-presentation?lang=${latest?.forstudiePresentationSprak || 'sv'}`,
                              '_blank',
                              'noopener'
                            );
                          }}
                          className="inline-flex items-center gap-1 sm:gap-1.5 bg-leadme-pink hover:bg-leadme-pink-dark text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium transition-colors whitespace-nowrap"
                          title="Öppna förstudie-presentationen i nytt fönster"
                        >
                          <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span className="hidden sm:inline">Se förstudie-presentation</span>
                          <span className="sm:hidden">Presentation</span>
                        </button>
                      )}
                      {statusBadge}
                      <svg
                        className={`w-5 h-5 text-gray-400 transform transition-transform flex-shrink-0 ${
                          expandedEstimat ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  {/* Mobil: visa action-knapp under */}
                  {actionButton && (
                    <div className="sm:hidden mt-2 pl-12">
                      {actionButton}
                    </div>
                  )}
                </div>
              );
            })()}

            {expandedEstimat && (
              <div className="border-t border-gray-200">
                {/* Flera estimat på samma projekt — t.ex. Ovanplan och
                    Bottenplan. Visas bara när det finns fler än ett, annars
                    är raden bara brus. */}
                {estimatSpar.length > 1 && (
                  <div className="px-3 sm:px-6 pt-4 flex flex-wrap gap-2">
                    {estimatSpar.map((e) => {
                      const aktiv = String(e._id) === String(valtEstimat?._id);
                      const ver = Number.isFinite(Number(e.version)) ? Number(e.version).toFixed(1) : '1.0';
                      const namn = (e.variantName || '').trim();
                      return (
                        <button
                          key={e._id}
                          onClick={() => setValtEstimatId(e._id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                            aktiv
                              ? 'bg-leadme-pink text-white border-leadme-pink'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {namn ? `${namn} ${ver}` : `Estimat ${ver}`}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="p-2 sm:p-6">
                  {/* Visa hela EstimatView inbäddat men låt rubrikerna ovan visa meta-info */}
                  {(() => {
                    const latest = valtEstimat;
                    if (!latest) return null;
                    return (
                      <EstimatView
                        embedded
                        hideSkiss={offerter?.length > 0}
                        estimatIdOverride={latest._id}
                        onFeedbackSent={() => window.location.reload()}
                      />
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}


        {/* Erbjudanden — visas här fram till projektstart, därefter via egen flik */}
        {(() => {
          const hasBookedHomeVisit = Boolean(procurement?.homeVisitWorkflow?.booking?.meetingAt);
          const hasProject = projects && projects.length > 0;

          // Så fort projektet finns tar menyvalet Erbjudanden över
          if (!hasBookedHomeVisit || hasProject) return null;

          return (
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white mt-6">
              <button
                onClick={() => setIsOffersExpanded(!isOffersExpanded)}
                className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-leadme-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <div className="min-w-0 text-left">
                    <span className="block font-semibold text-gray-900">Erbjudanden</span>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">Utforska våra samarbetspartners</p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-2 ${
                    isOffersExpanded ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isOffersExpanded && (
                <div className="border-t border-gray-200 divide-y divide-gray-200">
                  {erbjudanden.map((supplier) => (
                    <ErbjudandeRad
                      key={supplier._id}
                      supplier={supplier}
                      expanded={expandedSupplier === supplier._id}
                      onToggle={() => setExpandedSupplier(expandedSupplier === supplier._id ? null : supplier._id)}
                      onContact={handleContactRequest}
                      onInfo={handleShowInfo}
                      requestedAt={contactRequests[supplier._id]}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {showUnsavedDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-2">Du har osparade ändringar</h3>
              <p className="text-gray-500 text-sm mb-6">
                Om du lämnar sidan nu kommer dina ändringar att gå förlorade.
              </p>
              <div className="flex flex-col space-y-3">
                <button
                  onClick={() => {
                    setShowUnsavedDialog(false);
                  }}
                  className="w-full px-4 py-3 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink/90 font-medium"
                >
                  Stanna kvar
                  <span className="block text-pink-200 text-sm font-normal">Fortsätt redigera</span>
                </button>
                <button
                  onClick={() => {
                    // Ignorera ändringar
                    if (shoppingListRef.current) {
                      shoppingListRef.current.setHasUnsavedChanges(false);
                    }
                    if (rumsaRef.current) {
                      rumsaRef.current.setHasUnsavedChanges(false);
                    }
                    setShowUnsavedDialog(false);
                    navigate(pendingTabIndex);
                  }}
                  className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Lämna ändå
                  <span className="block text-gray-500 text-sm">Ändringar sparas inte</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <ErbjudandeModaler {...modalProps} />
        {showMaterialSupplierEmailModal && selectedMaterialSupplierContact && (
          <StandardEpost
            isOpen={showMaterialSupplierEmailModal}
            onClose={() => {
              setShowMaterialSupplierEmailModal(false);
              setSelectedMaterialSupplierContact(null);
              setSelectedMaterialSupplierProject(null);
            }}
            recipient={selectedMaterialSupplierContact.email}
            recipientName={`${selectedMaterialSupplierContact.firstName} ${selectedMaterialSupplierContact.lastName}`}
            recipientType="Materialleverantör"
            projectName={selectedMaterialSupplierProject?.projectId ? `#${selectedMaterialSupplierProject.projectId}` : ''}
            projectId={selectedMaterialSupplierProject ? selectedMaterialSupplierProject._id : undefined}
            allRecipients={getAllProjectRecipients(selectedMaterialSupplierProject, selectedMaterialSupplierContact)}
            showTeamMembers={true}
          />
        )}

        {/* Offert Modal */}
        {showOffertModal && selectedOffert && (
          <div className="fixed inset-0 bg-black bg-opacity-50 sm:flex sm:items-center sm:justify-center sm:p-4 z-50 flex items-end sm:items-center">
            <div
              className="bg-white sm:rounded-lg rounded-t-2xl max-w-4xl w-full max-h-[90vh] sm:max-h-[90vh] overflow-hidden"
              style={{ animation: window.innerWidth < 640 ? 'slideUp 0.3s ease-out' : undefined }}
            >
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold">
                  Offert från {selectedOffert.builderId?.companyName || 'Byggföretag'}
                </h3>
                <button
                  onClick={() => {
                    setShowOffertModal(false);
                    setSelectedOffert(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                <Offert
                  embedded
                  offertIdOverride={selectedOffert._id}
                  onCloseAfterSuccess={() => {
                    setShowOffertModal(false);
                    setSelectedOffert(null);
                    refreshOfferter();
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Jämför offerter - Urvals-modal */}
        {showCompareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Välj offerter att jämföra</h3>
                <button
                  onClick={() => {
                    setShowCompareModal(false);
                    setSelectedOffersForComparison([]);
                    scrollRefs.current = []; // Rensa scroll refs
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <p className="text-gray-600 mb-4">Välj 2-3 offerter som du vill jämföra sida vid sida:</p>
                <div className="space-y-3">
                  {(() => {
                    // Inkludera ALLA offerter (även obsolete) för jämförelse
                    const allOffersForModal = Array.isArray(offerter) ? offerter.filter(offer => 
                      ['sent', 'viewed', 'accepted', 'declined', 'commented', 'obsolete'].includes(offer.status)
                    ) : [];
                    
                    // Hjälpfunktion: kolla om en offert är/har varit godkänd
                    const isOfferAccepted = (offer) => offer.status === 'accepted' || offer.previousStatus === 'accepted';
                    
                    // Filtrera offerter för jämförelse: visa alla om ingen är godkänd, annars bara från godkänt byggföretag
                    const acceptedOffer = allOffersForModal.find(isOfferAccepted);
                    const acceptedBuilderId = acceptedOffer?.builderId?._id || acceptedOffer?.builderId;
                    
                    // Filtrera offerter för jämförelse:
                    // - Om en offert är godkänd: visa alla versioner från det godkända byggföretaget
                    // - Om showObsoleteOffers är true: visa alla offerter
                    // - Annars: visa alla utom obsolete
                    const comparableOffers = acceptedOffer
                      ? allOffersForModal.filter(offer => {
                          const offertBuilderId = offer.builderId?._id || offer.builderId;
                          return offertBuilderId === acceptedBuilderId;
                        })
                      : showObsoleteOffers
                        ? allOffersForModal
                        : allOffersForModal.filter(offer => offer.status !== 'obsolete');
                    
                    return comparableOffers.map((offert) => (
                      <label key={offert._id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedOffersForComparison.includes(offert._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (selectedOffersForComparison.length < 3) {
                                setSelectedOffersForComparison([...selectedOffersForComparison, offert._id]);
                              }
                            } else {
                              setSelectedOffersForComparison(selectedOffersForComparison.filter(id => id !== offert._id));
                            }
                          }}
                          disabled={!selectedOffersForComparison.includes(offert._id) && selectedOffersForComparison.length >= 3}
                          className="mr-3 h-4 w-4 text-leadme-pink focus:ring-leadme-pink border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <Avatar entity={offert.builderId} size="sm" />
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {offert.builderId?.companyName || 'Okänd byggare'}
                              </h4>
                              <p className="text-sm text-gray-500">
                                Version {offert.version} • Skickad {new Date(offert.sentAt).toLocaleDateString('sv-SE')}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* Status-badge */}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          (offert.status === 'accepted' || offert.previousStatus === 'accepted')
                            ? 'bg-green-100 text-green-800'
                            : offert.status === 'obsolete'
                              ? 'bg-gray-100 text-gray-600'
                              : offert.status === 'viewed'
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-blue-100 text-blue-800'
                        }`}>
                          {(offert.status === 'accepted' || offert.previousStatus === 'accepted')
                            ? '✅ Bokat byggare'
                            : offert.status === 'obsolete'
                              ? '📄 Äldre'
                              : offert.status === 'viewed'
                                ? '👁️ Öppnad'
                                : '✨ Ny'}
                        </span>
                      </label>
                    ));
                  })()}
                </div>
                {selectedOffersForComparison.length >= 3 && (
                  <p className="text-sm text-amber-600 mt-3">
                    ⚠️ Du kan jämföra max 3 offerter åt gången
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-600">
                  {selectedOffersForComparison.length} av 3 offerter valda
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowCompareModal(false);
                      setSelectedOffersForComparison([]);
                      scrollRefs.current = []; // Rensa scroll refs
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={() => {
                      if (selectedOffersForComparison.length >= 2) {
                        setShowCompareModal(false);
                        setShowComparisonView(true);
                      }
                    }}
                    disabled={selectedOffersForComparison.length < 2}
                    className={`px-4 py-2 rounded-md font-medium ${
                      selectedOffersForComparison.length >= 2
                        ? 'bg-leadme-pink text-white hover:bg-leadme-pink-dark'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Jämför offerter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Jämförelse-vy - Fullskärms-modal */}
        {showComparisonView && (
          <div className="fixed inset-0 bg-white z-50 overflow-hidden">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white shadow-sm">
                <h2 className="text-xl font-semibold text-gray-900">Jämför Offerter</h2>
                
                {/* Synkroniserad scrollning toggle */}
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <span className="text-sm text-gray-600">Synkad scrollning</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={syncScrollEnabled}
                      onClick={() => setSyncScrollEnabled(!syncScrollEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:ring-offset-2 ${
                        syncScrollEnabled ? 'bg-leadme-pink' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          syncScrollEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </label>
                  
                  <button
                    onClick={() => {
                      setShowComparisonView(false);
                      setSelectedOffersForComparison([]);
                      scrollRefs.current = []; // Rensa scroll refs
                    }}
                    className="text-gray-400 hover:text-gray-600 p-2"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Jämförelse-innehåll */}
              <div className="flex-1 overflow-hidden">
                <div className={`h-full grid gap-4 p-4 ${
                  selectedOffersForComparison.length === 2 
                    ? 'grid-cols-1 md:grid-cols-2' 
                    : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                }`}>
                  {selectedOffersForComparison.map((offertId, index) => {
                    const offert = offerter.find(o => o._id === offertId);
                    if (!offert) return null;
                    
                    return (
                      <div key={offertId} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        {/* Offert-header */}
                        <div className="bg-gray-50 p-4 border-b border-gray-200">
                          <div className="flex items-center space-x-3">
                            <Avatar entity={offert.builderId} size="sm" />
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {offert.builderId?.companyName || 'Okänd byggare'}
                              </h3>
                              <p className="text-sm text-gray-500">
                                Version {offert.version} • {new Date(offert.sentAt).toLocaleDateString('sv-SE')}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Offert-innehåll med synkroniserad scrollning */}
                        <div 
                          ref={(el) => scrollRefs.current[index] = el}
                          className="overflow-y-auto h-96 md:h-auto" 
                          style={{ height: 'calc(100vh - 200px)' }}
                          onScroll={(e) => handleSyncScroll(index, e.target.scrollTop)}
                        >
                          <Offert 
                            embedded 
                            offertIdOverride={offert._id}
                            comparisonMode={true}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Offertuppföljningsmöte - Använd SuggestTimeModal direkt för kunder */}
        <SuggestTimeModal
          show={showOffertFollowupModal}
          onClose={() => setShowOffertFollowupModal(false)}
          onSubmit={handleOffertFollowupSubmit}
          title="Föreslå tider för offertuppföljningsmöte"
          description="Välj 2–3 tider som passar er för att diskutera era offerter med byggkonsulten. Förslag visas ons–fre kl. 11–12 eller 14–15 de kommande två veckorna."
          maxProposals={3}
          mode="pregenerated"
          pregeneratedSlots={generateOffertMeetingOptions()}
        />

        {/* Estimat-mötesbokning modal - öppnas från "Boka genomgång"-knappen */}
        <MeetingBookingModal
          isOpen={showEstimatMeetingModal}
          onClose={() => {
            setShowEstimatMeetingModal(false);
            setEstimatMeetingSlots([]);
          }}
          meetingSlots={estimatMeetingSlots}
          onSlotChange={setEstimatMeetingSlots}
          availableSlots={generateOffertMeetingOptions()}
          maxSlots={3}
          minSlots={2}
          mode="submit"
          onSubmit={handleEstimatMeetingSubmit}
          submitting={estimatMeetingSubmitting}
        />

        {/* Toast Notification */}
        {toast && (
          <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-[10000] ${
            toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            <div className="flex items-center">
              <span className="mr-2">{toast.type === 'success' ? '✅' : '❌'}</span>
              <span>{toast.message}</span>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// OffertCard komponent
const OffertCard = ({ offert, hasAcceptedOffer, acceptedBuilderId, onClick }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Okänt datum';
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusInfo = (status, offert) => {
    // Specialhantering för obsolete-offerter: visa baserat på previousStatus
    if (status === 'obsolete') {
      if (offert.previousStatus === 'accepted') {
        return { text: 'Bokat byggare', color: 'bg-green-100 text-green-800', icon: '✅' };
      }
      // Annars visa en äldre version-status
      return { text: 'Äldre version', color: 'bg-gray-100 text-gray-600', icon: '📄' };
    }
    
    switch (status) {
      case 'sent':
        return { text: 'Ny offert', color: 'bg-blue-100 text-blue-800', icon: '✨' };
      case 'viewed':
        return { text: 'Öppnad', color: 'bg-orange-100 text-orange-800', icon: '👁️' };
      case 'accepted':
        return { text: 'Bokat byggare', color: 'bg-green-100 text-green-800', icon: '✅' };
      case 'declined':
        return { text: 'Avböjt', color: 'bg-red-100 text-red-800', icon: '❌' };
      case 'commented':
        // ÖVERGRIPANDE STATUS: Kolla om tråden behöver svar från kunden
        const messages = offert?.offertThread?.messages || [];
        let anyThreadNeedsReply = false;
        let isLastMessageMarkedAsOK = false;
        
        if (messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          // Kolla om SENASTE meddelandet är "Markerad som OK"
          isLastMessageMarkedAsOK = lastMessage?.message === '✓ Markerad som OK';
          
          if (!isLastMessageMarkedAsOK) {
            const consultantRepliedLast = lastMessage?.by === 'consultant';
            anyThreadNeedsReply = consultantRepliedLast; // Kunden behöver agera
          }
        }
        
        if (isLastMessageMarkedAsOK) {
          // SENASTE meddelandet är "Markerad som OK" → "Feedback OK" (grön)
          return { text: 'Feedback OK', color: 'bg-green-100 text-green-800', icon: '✅' };
        } else if (anyThreadNeedsReply) {
          // Övergripande "Ej svarat" → Kunden ser "Svar från byggkonsult" (grön)
          return { text: 'Svar från byggkonsult', color: 'bg-green-100 text-green-800', icon: '💬' };
        } else {
          // Övergripande "Svarat" → Kunden ser "Feedback skickad" (gul)
          return { text: 'Feedback skickad', color: 'bg-yellow-100 text-yellow-800', icon: '💬' };
        }
      default:
        return { text: 'Ny offert', color: 'bg-blue-100 text-blue-800', icon: '✨' };
    }
  };

  const statusInfo = getStatusInfo(offert.status, offert);

  /**
   * ETT TILLÄGG DÄMPAS ALDRIG.
   *
   * Både `isInactive` och `isNotTheAcceptedOffer` betyder "den här förlorade,
   * eller är ersatt" — och inget av det gäller en tilläggsoffert. Den lever vid
   * sidan av den godkända och är det enda kunden förväntas svara på just nu.
   * Utan undantaget hade tillägget legat halvgenomskinligt bland de byggare som
   * inte fick jobbet.
   */
  const arTillagg = arExtra(offert);

  // Kontrollera om denna offert ska vara inaktiv
  // Inaktiv endast om det finns godkänd offert OCH denna är från ett ANNAT byggföretag
  const offertBuilderId = offert.builderId?._id || offert.builderId;
  const isFromAcceptedBuilder = acceptedBuilderId && offertBuilderId === acceptedBuilderId;
  const isInactive = !arTillagg && hasAcceptedOffer && !isFromAcceptedBuilder && offert.status !== 'accepted';

  // 🔥 Kolla om offerten är obsolete (historisk version)
  const isObsolete = offert.status === 'obsolete';

  // 🔥 Kolla om denna offert INTE är den godkända (men fortfarande klickbar)
  const isAcceptedOffer = offert.status === 'accepted' || offert.previousStatus === 'accepted';
  const isNotTheAcceptedOffer = !arTillagg && hasAcceptedOffer && !isAcceptedOffer;

  return (
    <div className={`bg-white border rounded-lg p-4 transition-shadow relative ${
      isInactive
        ? 'opacity-50 cursor-not-allowed border-gray-200'
        : isNotTheAcceptedOffer
          ? 'opacity-60 hover:opacity-80 hover:shadow-md cursor-pointer border-gray-300 bg-gray-50'
          : isObsolete
            ? 'opacity-70 hover:opacity-100 hover:shadow-md cursor-pointer border-gray-300 border-dashed bg-gray-50'
            : 'hover:shadow-md cursor-pointer border-gray-200'
    }`}>
      {/* Header med logotyp och företagsnamn */}
      <div className="flex items-center space-x-3 mb-4">
        <div className="flex-shrink-0">
          <Avatar 
            entity={offert.builderId} 
            size="lg" 
            shape="rectangle"
            className="w-16 h-16 rounded-lg border border-gray-200"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-semibold text-gray-900 break-words">
            {offert.builderId?.companyName || 'Byggföretag'}
          </h4>
          <p className="text-sm text-gray-500">
            Version {offert.version || '1.0'}
          </p>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color} mt-1`}>
            <span className="mr-1">{statusInfo.icon}</span>
            {statusInfo.text}
          </span>
        </div>
      </div>

      {/* Datum information */}
      <div className="space-y-2 mb-4">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Skickat:</span> {formatDate(offert.sentAt)}
        </div>
        {offert.expiresAt && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Giltig till:</span> {formatDate(offert.expiresAt)}
          </div>
        )}
      </div>

      {/* Visa offert knapp */}
      <button
        onClick={isInactive ? undefined : onClick}
        disabled={isInactive}
        className={`w-full py-2 px-4 rounded-lg transition-colors font-medium text-sm ${
          isInactive 
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
            : 'bg-leadme-pink text-white hover:bg-leadme-pink-dark'
        }`}
      >
        {isInactive ? 'Inaktiv' : 'Visa offert'}
      </button>
    </div>
  );
};

const InfoRow = ({ label, value, message, messageColor }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-100">
    <span className="text-gray-600">{label}</span>
    <div className="text-right">
      <span className="font-medium text-gray-900">{value}</span>
      {message && (
        <p className={`text-sm ${messageColor}`}>
          {message}
        </p>
      )}
    </div>
  </div>
);

const TeamMember = ({ entity, name, role, email, company, user, project, phone, hideContactInfo }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [showPhoneCopyTooltip, setShowPhoneCopyTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');

  // Hantera e-post för teammedlemmar
  const handleEmailClick = (e) => {
    e.stopPropagation();
    setShowEmailModal(true);
  };

  // Funktion för att kopiera e-post till urklipp
  const handleCopyEmail = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(email).then(() => {
      setTooltipContent('E-post kopierad!');
      setShowCopyTooltip(true);
      setTimeout(() => setShowCopyTooltip(false), 2000);
    });
  };

  // Hämta telefonnummer baserat på roll
  const getPhoneNumber = () => {
    if (phone) {
      return phone;
    }
    let phoneNumber = '';
    if (role === 'Byggkonsult') {
      phoneNumber = project?.consultant?.phone || '';
    } else if (role === 'Inredningsarkitekt') {
      phoneNumber = project?.architect?.phone || '';
    } else if (role === 'Byggare') {
      phoneNumber = project?.builderContactPerson?.phone || project?.builder?.contactPerson?.phone || '';
    }
    return phoneNumber || 'Telefonnummer finns ej';
  };

  // Hantera kopiering av telefonnummer
  const handlePhoneClick = (e) => {
    e.stopPropagation();
    const phoneNumber = getPhoneNumber();
    if (phoneNumber && phoneNumber !== 'Telefonnummer finns ej') {
      navigator.clipboard.writeText(phoneNumber).then(() => {
        setTooltipContent('Telefonnummer kopierat!');
        setShowPhoneCopyTooltip(true);
        setTimeout(() => setShowPhoneCopyTooltip(false), 2000);
      });
    } else {
      setTooltipContent('Telefonnummer finns ej');
      setShowPhoneCopyTooltip(true);
      setTimeout(() => setShowPhoneCopyTooltip(false), 2000);
    }
  };

  return (
    <div className="flex items-center space-x-3 py-1">
      <div className="flex-shrink-0">
        <Avatar entity={entity} size="sm" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 break-words">{name || (entity && (entity.firstName || entity.lastName) ? `${entity.firstName || ''} ${entity.lastName || ''}` : 'Namnlös användare')}</p>
        <div className="flex flex-col text-xs text-gray-500">
          {role && <span>{role}</span>}
          {company && <span className="text-gray-400">{company}</span>}
        </div>
      </div>
      {!hideContactInfo && (
        <div className="flex space-x-2">
          {/* Telefonikon */}
          <div className="relative group">
            <button
              className="p-1.5 text-gray-500 hover:text-leadme-pink transition-colors hover:bg-gray-50 rounded-full"
              onClick={handlePhoneClick}
              title="Kopiera telefonnummer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
              </svg>
            </button>
            {/* Tooltip vid kopiering */}
            {showPhoneCopyTooltip && (
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap z-20">
                {tooltipContent}
              </div>
            )}
          </div>
          {/* E-postikon (öppna modal) */}
          <div className="relative group">
            <button
              className="p-1.5 text-gray-500 hover:text-leadme-pink transition-colors hover:bg-gray-50 rounded-full"
              onClick={handleEmailClick}
              title="Skicka e-post"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </button>
          </div>
          {/* Kopiera e-postikon */}
          <div className="relative group">
            <button
              className="p-1.5 text-gray-500 hover:text-leadme-pink transition-colors hover:bg-gray-50 rounded-full"
              onClick={handleCopyEmail}
              title="Kopiera e-postadress"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
            </button>
            {/* Tooltip vid kopiering */}
            {showCopyTooltip && (
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap z-20">
                {tooltipContent}
              </div>
            )}
          </div>
        </div>
      )}
      {/* E-postmodal */}
      {!hideContactInfo && showEmailModal && (
        <StandardEpost
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          recipient={email}
          recipientName={name}
          recipientType={role}
          allRecipients={getAllProjectRecipients(project, entity)}
          projectName={project?.projectId ? `#${project.projectId}` : ''}
          projectId={project ? project._id : undefined}
          showTeamMembers={user?.role === 'byggkonsult' || user?.role === 'inredningsarkitekt'}
        />
      )}
    </div>
  );
};

const getDocumentStatus = (type, document) => {
  // Ta bort specialhanteringen för tidplan
  if (!document) {
    return {
      status: 'Saknas',
      color: 'bg-gray-500',
      textColor: 'text-gray-500',
      bgColor: 'bg-gray-50'
    };
  }

  // Hantera special-case för online-status (används för Underlag och Avtal)
  if (document.status === 'online') {
    return {
      status: 'Online',
      color: 'bg-green-500',
      textColor: 'text-green-500',
      bgColor: 'bg-green-50'
    };
  }

  // Specialhantering för underlag/avtal som är offline/saknas
  if ((type === 'Underlag' || type === 'Avtal') && (document.status === 'missing' || document.status === 'offline')) {
    return {
      status: 'Offline',
      color: 'bg-red-500',
      textColor: 'text-red-500',
      bgColor: 'bg-red-50'
    };
  }

  switch (document.status) {
    case 'completed':
      return {
        status: 'Klart',
        color: 'bg-green-500',
        textColor: 'text-green-500',
        bgColor: 'bg-green-50'
      };
    case 'active':
        return {
          status: 'Aktiv',
          color: 'bg-green-500',
          textColor: 'text-green-500',
          bgColor: 'bg-green-50'
        };
    case 'in_progress':
      return {
        status: 'Pågående',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-600',
        bgColor: 'bg-yellow-50'
      };
    case 'getting_started':
      return {
        status: 'Kom igång',
        color: 'bg-leadme-pink-dark',
        textColor: 'text-leadme-pink-dark',
        bgColor: 'bg-leadme-pink-dark',
        titleColor: 'text-white'
      };
    case 'uploaded':
      return {
        status: 'Uppladdad',
        color: 'bg-blue-500',
        textColor: 'text-blue-500',
        bgColor: 'bg-blue-50'
      };
    case 'missing':
    default:
      return {
        status: 'Saknas',
        color: 'bg-gray-500',
        textColor: 'text-gray-500',
        bgColor: 'bg-gray-50'
      };
  }
};

const DocumentCard = ({ title, status, progress, onClick, documentType }) => {
  let actualStatus = status;
  if ((documentType === 'underlag' || documentType === 'avtal') && (status === undefined || status === 'loading')) {
    // Visa spinner
    return (
      <div className="bg-gray-50 p-4 rounded-lg opacity-75 cursor-not-allowed flex items-center justify-between animate-pulse">
        <h4 className="font-medium text-gray-900">{title}</h4>
        <span className="flex items-center">
          <svg className="w-4 h-4 mr-1 animate-spin text-leadme-pink" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-leadme-pink">Kontrollerar...</span>
        </span>
      </div>
    );
  }
  // ... befintlig kod ...
  const config = (documentType === 'underlag' || documentType === 'avtal')
    ? getDocumentStatus(title, { status: actualStatus }) 
    : getDocumentStatus(title, { status });
  const isDisabled = config.status.toLowerCase() === 'saknas' || 
                     ((documentType === 'underlag' || documentType === 'avtal') && config.status.toLowerCase() === 'offline');
  return (
    <div
      onClick={isDisabled ? undefined : onClick}
      className={`${config.bgColor} p-4 rounded-lg ${isDisabled ? 'cursor-not-allowed opacity-75' : 'cursor-pointer hover:shadow-md'} transition-all duration-200`}
    >
      <div className="flex justify-between items-center mb-3">
        <h4 className={`font-medium ${config.titleColor || 'text-gray-900'}`}>{title}</h4>
        <span className={`px-2 py-1 rounded-full text-xs ${config.textColor} bg-white`}>
          {config.status}
        </span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${config.color} transition-all duration-500`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const calculateProgress = (document, type) => {
  if (!document) return 0;
  
  // Specialhantering för tidplan
  if (type === 'Tidplan' && document.status !== 'missing') {
    return 100;  // Alltid 100% om den finns
  }

  // Normal hantering för andra dokument
  switch (document.status) {
    case 'completed': return 100;
    case 'in_progress': return 50;
    case 'uploaded': return 25;
    default: return 0;
  }
};

/**
 * Projektstatus i kundvyn.
 *
 * Räknar likadant som byggkonsultens projektöversikt och de övriga kortvyerna —
 * se features/workspace/utils/projectStatus.js.
 *
 * Tidigare auto-avslutades projektet så fort slutdatumet passerat. Kunden såg då
 * "Avslutad · Projektet är avslutat" medan byggkonsulten samtidigt såg
 * "Pågående · Försenat (108 dgr över)" på samma projekt. Att markera ett projekt
 * klart är ett aktivt beslut som byggkonsulten tar; passerat slutdatum utan
 * sådant beslut betyder försenat.
 */
const getProjectTimeStatus = (project) => {
  // Utan publicerad tidplan visas Projektering, som förut
  const isTimelinePublished = project.timeline?.status !== 'missing' && project.timeline?.exists;
  const harDatum = isTimelinePublished && project.timeline?.startDate && project.timeline?.endDate;

  if (!harDatum && !project.completionStatus) {
    return { status: 'Projektering', message: '', color: 'text-gray-600' };
  }

  const s = beraknaProjektStatus(project);

  // Kundvyn använder textfärger i stället för bakgrundsklasser
  const fargPerStatus = {
    'Avslutat': 'text-green-600',
    'Anmärkning/Tillägg': 'text-amber-600',
    'Planerad': 'text-blue-600',
    'Projektering': 'text-gray-600'
  };

  return {
    status: s.subStatus ? `${s.status} · ${s.subStatus}` : s.status,
    message: s.message,
    color: s.overdue ? 'text-orange-600' : (fargPerStatus[s.status] || 'text-leadme-orange')
  };
};

export default MyProjects; 

